import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
// import { Worker, Viewer, SpecialZoomLevel } from '@react-pdf-viewer/core';
// import '@react-pdf-viewer/core/lib/styles/index.css';
import connectedGif from "../assets/images/connected.gif";
import connectedImg from "../assets/images/connectedImg.gif";
import DisconnectedImg from "../assets/images/DisconnectedImg.gif";
import VoiceVisualizer from "../components/VoiceVisualizer";
import creationModeAnimation from "../assets/images/Animation.json";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import {
  ChevronDown,
  ClipboardCopy,
  ThumbsUp,
  ThumbsDown,
  SendHorizontal,
  CircleCheck,
  Image,
  FileText,
  Mic,
  User,
  Menu,
  Clock,
  Star,
  Plus,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";
import configData from "../configData";
import { chatApi } from "../services/socialApi";
import realtimeService from "../services/realtimeService";
import { isBackgroundRequest } from "../utils/backgroundRequest";
import { initGameRealtime } from "../services/gameRealtimeService";
import AgentPoster from "../assets/images/AgentPoster.png";
import { animateScroll as scrollLibrary } from "react-scroll";

// autobahn was imported here and never referenced once. It is a WAMP client
// and it pulls cbor, tweetnacl and msgpack5 with it, none of which any app
// file imports directly, so the whole tree was in the entry bundle for a
// symbol nothing used. The worker that genuinely needs it
// (src/pages/crossbarWorker.js) imports it itself and builds separately.
import CryptoJS from "crypto-js";

import NewHome from "./newHomeforDemo";

import OtpAuthModal from "./OtpAuthModal";
// react-pdf, its two stylesheets and the worker line all moved into
// ./PdfPreview so they load with it. Left here they would have kept pdf.js in
// the entry bundle even with the viewer itself lazy.
import Agents from "../components/Agent/Agents";
import CreateAgentForm from "../pages/CreateAgentForm";
import CreditSystem from "./Credits";
import { readPrerenderData, publishPrerenderData } from '../utils/prerenderData';
import SIDEBAR_LINKS from './sidebarLinks';

// lottie-react (298KB of lottie-web) and react-pdf (~170KB of pdf.js) are
// loaded on demand, not in the entry bundle. Both render only in states the
// first pass cannot be in, which is what makes lazy() safe here where
// routeChunks.js warns it is not: the Lottie is gated on
// agent_status === "Creation Mode" (absent from the seeded currentAgent, and
// "Creation mode" appears nowhere in the captured homepage) and the PDF
// preview on `uploadedPdf`, which only a user picking a file can set. A
// Suspense boundary that never suspends during hydration costs nothing.
//
// Below the imports, not among them: eslint's import/first counts every
// import after a statement as "in body of module" and CRA makes that fatal.
const Lottie = React.lazy(() => import("lottie-react"));
const PdfPreview = React.lazy(() => import("./PdfPreview"));

const SECRET_KEY = process.env.REACT_APP_SECRET_KEY;

const TypeWriterForSubtitle = ({
  text,
  duration,
  isIdle,
  shouldStart = true,
  onComplete,
}) => {
  const [displayText, setDisplayText] = useState("");
  const charIndexRef = useRef(0);
  const intervalRef = useRef(null);
  const hasCompletedRef = useRef(false);
  const prevTextRef = useRef(text);
  const prevDurationRef = useRef(duration);

  const stopAnimation = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Start animation
  const startAnimation = useCallback(() => {
    if (
      hasCompletedRef.current ||
      !text ||
      duration <= 0 ||
      isIdle ||
      !shouldStart
    ) {
      return;
    }

    stopAnimation();

    const charDelay = (duration * 1000) / text.length;
    let currentText = "";

    intervalRef.current = setInterval(() => {
      if (charIndexRef.current >= text.length) {
        stopAnimation();
        hasCompletedRef.current = true;
        onComplete?.();
        return;
      }

      currentText += text[charIndexRef.current];
      setDisplayText(currentText);
      charIndexRef.current += 1;
    }, charDelay);
  }, [text, duration, isIdle, shouldStart, onComplete, stopAnimation]);

  useEffect(() => {
    if (text !== prevTextRef.current) {
      charIndexRef.current = 0;
      hasCompletedRef.current = false;
      setDisplayText("");
      prevTextRef.current = text;

      if (shouldStart && !isIdle) {
        startAnimation();
      }
    } else if (
      Math.abs(duration - prevDurationRef.current) >
      prevDurationRef.current * 0.1
    ) {
      prevDurationRef.current = duration;
      if (shouldStart && !isIdle && !hasCompletedRef.current) {
        startAnimation();
      }
    }

    return stopAnimation;
  }, [text, shouldStart, isIdle, startAnimation, stopAnimation]);

  if (isIdle || !shouldStart) {
    return <div>{text}</div>;
  }

  return <div>{displayText}</div>;
};

const ThinkingProcessContainer = ({
  thinkingMessages,
  onToggleMain,
  onToggleIndividual,
  isMainExpanded,
  isContainerCompleted,
}) => {
  const thinkingContentRef = useRef(null);
  const [displayedTexts, setDisplayedTexts] = useState({});
  const [animatingSteps, setAnimatingSteps] = useState(new Set());
  const [fullyDisplayedSteps, setFullyDisplayedSteps] = useState(new Set());
  const [liveTime, setLiveTime] = useState(0);

  useEffect(() => {
    console.log("🔍 ThinkingProcessContainer Debug:");
    console.log("  - isContainerCompleted:", isContainerCompleted);
    console.log("  - thinkingMessages length:", thinkingMessages.length);
    console.log("  - thinkingMessages:", thinkingMessages);
    console.log(
      "  - Individual step completion:",
      thinkingMessages.map((msg) => ({
        id: msg.id,
        isCompleted: msg.isCompleted,
      })),
    );
  }, [isContainerCompleted, thinkingMessages]);

  const isReallyCompleted = useMemo(() => {
    console.log("🧮 Computing isReallyCompleted:");
    console.log("  - isContainerCompleted prop:", isContainerCompleted);

    if (isContainerCompleted === true) {
      console.log("  ✅ Completed via isContainerCompleted prop");
      return true;
    }

    if (thinkingMessages.length > 0) {
      const allStepsCompleted = thinkingMessages.every(
        (msg) => msg.isCompleted === true,
      );
      console.log("  - All steps completed:", allStepsCompleted);

      if (allStepsCompleted) {
        console.log("  ✅ Completed via all steps completed");
        return true;
      }
    }

    console.log("  ❌ Not completed - still thinking");
    return false;
  }, [isContainerCompleted, thinkingMessages]);

  useEffect(() => {
    if (isMainExpanded && thinkingContentRef.current) {
      if (isReallyCompleted) {
        setTimeout(() => {
          thinkingContentRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }, 100);
      } else {
        setTimeout(() => {
          thinkingContentRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "end",
          });
        }, 100);
      }
    }
  }, [isMainExpanded, isReallyCompleted, thinkingMessages.length]);

  useEffect(() => {
    if (isMainExpanded && !isReallyCompleted && thinkingMessages.length > 0) {
      setTimeout(() => {
        thinkingContentRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }, 200);
    }
  }, [thinkingMessages.length]);

  useEffect(() => {
    if (isMainExpanded && !isReallyCompleted && thinkingMessages.length > 0) {
      setTimeout(() => {
        thinkingContentRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }, 200);
    }
  }, [thinkingMessages.length]);

  useEffect(() => {
    if (isReallyCompleted) {
      console.log("⏹️ Stopping timer - thinking completed");
      return;
    }

    const interval = setInterval(() => {
      setLiveTime((prev) => prev + 0.3);
    }, 300);

    return () => {
      clearInterval(interval);
    };
  }, [isReallyCompleted]);

  useEffect(() => {
    if (!isReallyCompleted && thinkingMessages.length > 0 && liveTime === 0) {
      console.log("🔄 Starting new thinking session");
    }
  }, [thinkingMessages, isReallyCompleted, liveTime]);

  useEffect(() => {
    const initialTexts = {};
    thinkingMessages.forEach((msg) => {
      initialTexts[msg.id] = "";
    });
    setDisplayedTexts(initialTexts);
    setAnimatingSteps(new Set());
    setFullyDisplayedSteps(new Set());
  }, [thinkingMessages]);

  const handleManualToggle = (stepId) => {
    const message = thinkingMessages.find((msg) => msg.id === stepId);

    onToggleIndividual(stepId);

    if (message && !message.isExpanded && message.content) {
      setAnimatingSteps((prev) => new Set([...prev, stepId]));

      setDisplayedTexts((prev) => ({
        ...prev,
        [stepId]: "",
      }));

      let index = 0;
      const text = message.content;

      const timer = setInterval(() => {
        if (index < text.length) {
          setDisplayedTexts((prev) => ({
            ...prev,
            [stepId]: text.slice(0, index + 1),
          }));
          index++;
        } else {
          clearInterval(timer);
          setAnimatingSteps((prev) => {
            const newSet = new Set(prev);
            newSet.delete(stepId);
            return newSet;
          });
          setFullyDisplayedSteps((prev) => new Set([...prev, stepId]));
          setDisplayedTexts((prev) => ({
            ...prev,
            [stepId]: text,
          }));
        }
      }, 20);
    }
  };

  const formatDuration = (durationInSeconds) => {
    if (!durationInSeconds) return "";
    if (durationInSeconds < 1) {
      return `${Math.round(durationInSeconds * 1000)}ms`;
    } else if (durationInSeconds < 60) {
      return `${durationInSeconds.toFixed(1)}s`;
    } else {
      const minutes = Math.floor(durationInSeconds / 60);
      const seconds = Math.round(durationInSeconds % 60);
      return `${minutes}m ${seconds}s`;
    }
  };

  const formatLiveTime = (timeInSeconds) => {
    if (timeInSeconds < 1) {
      return `${Math.round(timeInSeconds * 1000)}ms`;
    } else if (timeInSeconds < 60) {
      return `${timeInSeconds.toFixed(1)}s`;
    } else {
      const minutes = Math.floor(timeInSeconds / 60);
      const seconds = Math.round(timeInSeconds % 60);
      return `${minutes}m ${seconds}s`;
    }
  };

  const shouldAutoScroll = (content) => {
    if (!content) return false;
    const words = content.trim().split(/\s+/).length;
    const lines = content.split("\n").length;
    return words > 30 || lines > 2;
  };

  const getCurrentThinkingPreview = () => {
    if (!thinkingMessages || thinkingMessages.length === 0) return "";

    const latestStep = thinkingMessages[thinkingMessages.length - 1];

    if (!latestStep || !latestStep.content) return "";

    const words = latestStep.content.trim().split(/\s+/);
    const preview = words.slice(0, 6).join(" ");

    return words.length > 6 ? `${preview}...` : preview;
  };

  const totalDuration = thinkingMessages.reduce(
    (sum, msg) => sum + (msg.duration || 0),
    0,
  );
  const currentPreview = getCurrentThinkingPreview();

  useEffect(() => {
    console.log("🎯 Final render state:", {
      isReallyCompleted,
      isContainerCompleted,
      liveTime,
      totalDuration,
      thinkingMessagesCount: thinkingMessages.length,
    });
  }, [
    isReallyCompleted,
    isContainerCompleted,
    liveTime,
    totalDuration,
    thinkingMessages.length,
  ]);

  return (
    <div className="border border-gray-300 rounded-lg mb-4 overflow-hidden bg-gray-50">
      <button
        onClick={onToggleMain}
        className="w-full p-4 bg-gray-50 hover:bg-gray-100 text-left flex items-center justify-between transition-colors group"
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="flex gap-1 flex-shrink-0">
            {isReallyCompleted ? (
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  className="text-white"
                >
                  <path
                    d="M3 6L5 8L9 4"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            ) : (
              <>
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse delay-0"></div>
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse delay-200"></div>
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse delay-[400ms]"></div>
              </>
            )}
          </div>

          <div className="flex flex-col items-start min-w-0 flex-1">
            <span className="text-gray-700 font-medium flex-shrink-0">
              {isReallyCompleted
                ? "Thought process (completed)"
                : "Thought process"}
            </span>

            {!isMainExpanded && !isReallyCompleted && currentPreview && (
              <span className="text-xs text-gray-500 truncate max-w-full mt-1 font-mono">
                {currentPreview}
              </span>
            )}

            {!isMainExpanded &&
              isReallyCompleted &&
              (totalDuration > 0 || liveTime > 0) && (
                <span className="text-xs text-gray-500 mt-1">
                  {totalDuration > 0
                    ? formatDuration(totalDuration)
                    : formatLiveTime(liveTime)}
                </span>
              )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Live timing display - shows on the right side */}
          {!isReallyCompleted ? (
            <span className="text-xs text-gray-500 font-mono">
              {formatLiveTime(liveTime)}
            </span>
          ) : (
            <span className="text-xs text-green-600 font-mono">
              ✓{" "}
              {totalDuration > 0
                ? formatDuration(totalDuration)
                : formatLiveTime(liveTime)}
            </span>
          )}

          <div
            className={`transform transition-transform duration-200 ${
              isMainExpanded ? "rotate-180" : ""
            }`}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              className="text-gray-500"
            >
              <path
                d="M4 6L8 10L12 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
      </button>

      {isMainExpanded && (
        <div
          className="border-t border-gray-200 bg-white"
          ref={thinkingContentRef}
        >
          <div className="p-4 space-y-3">
            {/* Live timing display in expanded view */}
            {!isReallyCompleted ? (
              <div className="flex justify-between items-center bg-blue-50 p-2 rounded-md">
                <span className="text-sm text-blue-700 font-medium">
                  Thinking in progress...
                </span>
                <span className="text-sm text-blue-700 font-mono">
                  {formatLiveTime(liveTime)}
                </span>
              </div>
            ) : (
              <div className="flex justify-between items-center bg-green-50 p-2 rounded-md">
                <span className="text-sm text-green-700 font-medium">
                  ✓ Thinking completed
                </span>
                <span className="text-sm text-green-700 font-mono">
                  {totalDuration > 0
                    ? formatDuration(totalDuration)
                    : formatLiveTime(liveTime)}
                </span>
              </div>
            )}

            {thinkingMessages.map((message, index) => (
              <div
                key={message.id}
                className="border border-gray-200 rounded-md overflow-hidden"
              >
                <button
                  onClick={() => handleManualToggle(message.id)}
                  className="w-full p-3 bg-gray-50 hover:bg-gray-100 text-left flex items-center justify-between transition-colors text-sm"
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <div className="flex gap-1 flex-shrink-0">
                      {message.isCompleted || isReallyCompleted ? (
                        <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                          <svg
                            width="8"
                            height="8"
                            viewBox="0 0 8 8"
                            fill="none"
                            className="text-white"
                          >
                            <path
                              d="M2 4L3.5 5.5L6 2.5"
                              stroke="white"
                              strokeWidth="1"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                      ) : (
                        <>
                          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse delay-0"></div>
                          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse delay-200"></div>
                          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse delay-[400ms]"></div>
                        </>
                      )}
                    </div>
                    <span className="text-gray-600 font-medium flex-shrink-0">
                      Step {index + 1}
                    </span>
                    {!message.isExpanded && message.content && (
                      <span className="text-xs text-gray-500 truncate min-w-0 font-mono">
                        {message.content.slice(0, 50)}
                        {message.content.length > 50 ? "..." : ""}
                      </span>
                    )}
                  </div>

                  <div
                    className={`transform transition-transform duration-200 flex-shrink-0 ${
                      message.isExpanded ? "rotate-180" : ""
                    }`}
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      className="text-gray-400"
                    >
                      <path
                        d="M3 4.5L6 7.5L9 4.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </button>

                {message.isExpanded && (
                  <div className="border-t border-gray-200 bg-white">
                    <div
                      className={`p-3 ${
                        shouldAutoScroll(message.content)
                          ? "max-h-40 overflow-y-auto"
                          : ""
                      }`}
                    >
                      <div className="text-gray-700 whitespace-pre-wrap font-mono text-xs leading-relaxed">
                        {(() => {
                          if (animatingSteps.has(message.id)) {
                            return (
                              <>
                                {displayedTexts[message.id] || ""}
                                {displayedTexts[message.id] &&
                                  displayedTexts[message.id].length <
                                    (message.content?.length || 0) && (
                                    <span className="animate-pulse">|</span>
                                  )}
                              </>
                            );
                          }

                          if (
                            message.isCompleted ||
                            fullyDisplayedSteps.has(message.id) ||
                            isReallyCompleted
                          ) {
                            return message.content;
                          }

                          if (displayedTexts[message.id]) {
                            return displayedTexts[message.id];
                          }

                          return message.content;
                        })()}
                      </div>
                      {message.duration && (
                        <div className="mt-2 pt-2 border-t border-gray-200 text-xs text-gray-500">
                          Step duration: {formatDuration(message.duration)}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}

            {(totalDuration > 0 || liveTime > 0) && isReallyCompleted && (
              <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500 text-center">
                Total thinking time:{" "}
                {totalDuration > 0
                  ? formatDuration(totalDuration)
                  : formatLiveTime(liveTime)}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const getDownloadInfo = () => {
  return {
    href: "https://docs.hevolve.ai/downloads/",
    label: "Download Nunba & HARTOS",
  };
};

// The sidebar menu, declared once, in its own module so it can be tested
// against the router. See src/pages/sidebarLinks.js for why.

// `seedMessage` — an opening question handed in from another page (a research
// paper, a news story). It pre-fills the composer rather than auto-sending:
// the reader should see the question and be able to edit it before it goes,
// and auto-sending would also fire on every back-navigation to this URL.
const ChatInterface = ({ agentData, seedMessage }) => {
  const navigate = useNavigate();
  const { agentName } = useParams();

  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const textareaRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");

  // Apply the seed once, and only into an empty composer, so it never
  // clobbers something the user is part-way through typing.
  const seedAppliedRef = useRef(false);
  useEffect(() => {
    if (!seedMessage || seedAppliedRef.current) return;
    seedAppliedRef.current = true;
    setInputMessage((prev) => (prev ? prev : seedMessage));
  }, [seedMessage]);
  const [pdffileUrl, setpdfFileUrl] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState();

  const [currentThinkingId, setCurrentThinkingId] = useState(null);
  const [thinkingStartTime, setThinkingStartTime] = useState(null);

  const [shouldScroll, setShouldScroll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [requestId, setRequestId] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const messagesEndRef = useRef(null);
  const [userImage, setUserImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showAgentsOverlay, setShowAgentsOverlay] = useState(false);
  const [fileUrl, setFileUrl] = useState(null);
  const connectionRef = useRef(null);
  const [displayedText, setDisplayedText] = useState("");
  const [showContent, setShowContent] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("Disconnected");
  const [messagesauto, setMessagesauto] = useState([]);
  const [codeContent, setCodeContent] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState(null);
  const [allAgents, setAllAgents] = useState([]);
  // Seeded from what the prerenderer ACTUALLY had here.
  //
  // The effect below copies agentData in on mount, so the first render had this
  // undefined while the prerendered file, captured after effects, had the
  // agent. The visible difference is one paragraph:
  //
  //     <p>{currentAgent?.video_text}</p>
  //
  // empty on the client, filled in the file: "Hydration failed ... at p ... at
  // ChatInterface".
  //
  // Starting from the agentData PROP looked like enough and was not. This
  // component runs its own agent fetch as well, so the value the capture ended
  // up with is not always the prop, and seeding the prop turned a missing
  // paragraph into a paragraph with the wrong words: / went from #418 to #425,
  // a text mismatch, on every run. Seed what was actually rendered.
  const [currentAgent, setCurrentAgent] = useState(
    () => readPrerenderData('currentAgent') ?? agentData
  );
  const [duration, setDuration] = useState(0);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [decryptedUserId, setDecryptedUserId] = useState(null);
  const [decryptedPhone, setDecryptedPhone] = useState(null);
  const [decryptedEmail, setDecryptedEmail] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  // The width the PRERENDERER used, not this browser's. Reading
  // window.innerWidth here made the first client render depend on the visitor's
  // window while the saved file was captured at one fixed width, so every other
  // size disagreed and hydration threw the root away. Agent.js renders this
  // component on the homepage, so the homepage carried the fault too. The
  // resize effect below takes over with the real width straight after mount.
  // See src/utils/prerenderData.js.
  const [screenWidth, setScreenWidth] = useState(
    () => readPrerenderData('viewportWidth') || 1280
  );
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState("");
  // Read after mount, not during render. The prerenderer has no localStorage,
  // so the saved file is always the signed-out shape; a signed-in visitor
  // rendered the other one on the first pass and hydration discarded the root.
  const [token, setToken] = useState(null);
  useEffect(() => {
    try {
      setToken(localStorage.getItem("access_token"));
    } catch { /* private mode blocks localStorage; stay signed out */ }
  }, []);
  // After mount, same reason as access_token above: the prerenderer has no
  // localStorage, so reading it during render makes a signed-in visitor's first
  // pass differ from the saved file.
  const [refresh_token, setRefreshToken] = useState(null);
  useEffect(() => {
    try {
      setRefreshToken(localStorage.getItem("refresh_token"));
    } catch { /* private mode blocks localStorage */ }
  }, []);
  const [uploadedPdf, setUploadedPdf] = useState(null);
  const [pdfurl, setPdfurl] = useState(null);
  const [worker, setWorker] = useState(null);
  // Media mode parity with Nunba — single source of truth for the
  // chat surface mode.  Default is 'audio' so a cold boot lands the
  // user in the voice-first experience (visualizer animates while the
  // hive replies).  isTextMode + setIsTextMode are shims kept for the
  // 15+ existing call sites in this file; do NOT add new call sites
  // to the shim — read mediaMode directly.  Persisted under
  // 'hevolve_media_mode'; mirrors Nunba's 'nunba_media_mode' key
  // shape so a user roaming between desktop and web sees a familiar
  // default.
  // Starts at the default and picks the saved preference up after mount. A lazy
  // initialiser still runs during the FIRST render, so reading storage there
  // gave a returning visitor different markup from the prerendered file.
  const [mediaMode, setMediaMode] = useState('audio');
  const isTextMode = mediaMode === 'text';
  const setIsTextMode = (val) =>
    setMediaMode(typeof val === 'function'
      ? (prev) => (val(prev === 'text') ? 'text' : 'video')
      : (val ? 'text' : 'video'));

  // Restore, then persist, and SKIP THE FIRST persist run.
  //
  // With the read moved out of the initialiser, the write below would otherwise
  // put the default into storage before the saved value is ever read. Gating on
  // a flag the restore effect sets does not help: effects run in declaration
  // order inside one commit, so the flag is already true when the persist
  // effect runs, and that run still holds the OLD state. It wrote 'audio' over
  // the visitor's preference regardless.
  //
  // The final stored value comes out right either way, because restoring
  // re-renders and persists again, which is exactly why asserting the end state
  // proves nothing. The damage is the moment in between: storage says 'audio',
  // and a visitor who closes the tab in that window has lost the setting.
  // src/pages/__tests__/demopageMediaMode.test.js watches the writes.
  const skipFirstMediaModePersist = useRef(true);
  useEffect(() => {
    try {
      const saved = localStorage.getItem('hevolve_media_mode');
      if (saved) setMediaMode(saved);
    } catch (_e) { /* private mode blocks localStorage; keep the default */ }
  }, []);
  useEffect(() => {
    if (skipFirstMediaModePersist.current) {
      skipFirstMediaModePersist.current = false;
      return;
    }
    try { localStorage.setItem('hevolve_media_mode', mediaMode); } catch (_e) {}
  }, [mediaMode]);
  const [isTrialExpired, setIsTrialExpired] = useState(false);
  const [currentInteractionType, setCurrentInteractionType] = useState("text");
  const [credits, setCredits] = useState(0);
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [progress, setProgress] = useState(0);
  const [text, setText] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const recognitionRef = useRef(null);

  const [scale, setScale] = useState(0.4);
  const containerRef = useRef(null);
  const [isMediaEnded, setIsMediaEnded] = useState(false);
  const [showCreateAgentForm, setShowCreateAgentForm] = useState(false);
  // Same again: false first, then whatever storage says. See mediaMode above.
  const [authFromUrl, setAuthFromUrl] = useState(false);
  useEffect(() => {
    try {
      setAuthFromUrl(localStorage.getItem("auth_source") === "url");
    } catch { /* private mode blocks localStorage */ }
  }, []);

  const downloadInfo = useMemo(() => getDownloadInfo(), []);
  const [requestIdFromCrossbar, setRequestIdFromCrossbar] = useState(null);
  const [idleVideoUrl, setIdleVideoUrl] = useState(null);
  const [isPlayingResponse, setIsPlayingResponse] = useState(false);

  const [waitingText, setWaitingText] = useState(null);
  // Derived from the state above rather than read again. A second
  // window.innerWidth here was both another first-render environment read and a
  // value that could disagree with screenWidth after a resize.
  // Height as well as width. The voice visualizer is SQUARE, so in landscape
  // the short side is what constrains it, and sizing from width alone drew a
  // canvas far larger than the pane could show; the 80% cap then shrank what
  // was left. Seeded like screenWidth so the first render matches the capture.
  const [screenHeight, setScreenHeight] = useState(
    () => readPrerenderData('viewportHeight') || 800
  );
  useEffect(() => {
    publishPrerenderData('viewportHeight', screenHeight);
  }, [screenHeight]);

  const isSmallScreen = screenWidth < 768;

  // How the two panes split the width. Derived once because they have to agree:
  // the media pane collapsed to md:w-0 when there was no video while the chat
  // column stayed hardcoded at md:w-[60%], so once audio became the default and
  // videoUrl was normally absent, the avatar disappeared and the text kept
  // hugging the left with 40% of the screen empty beside it. Headings wrapped in
  // a column that had no reason to be narrow.
  //
  // Three states, and the panes must pick the same one:
  //   an upload      the media pane takes the full width, chat sits below it
  //   a video        30 / 60, the arrangement this page was designed around
  //   audio only     no media pane at all, so chat takes the whole width
  const hasUploadPreview = Boolean(uploadedImage || uploadedPdf);
  // The media pane is never empty: with a video it shows the avatar, without one
  // it shows the VoiceVisualizer. So it keeps the same 30/60 split either way,
  // and the visualizer lands where the avatar was, which is what made the hero
  // and this page line up in the first place.
  //
  // It used to be `md:w-0 md:overflow-hidden` when there was no video, which
  // collapsed nothing: flex items default to `min-width: auto`, so a flex child
  // with content will not shrink past its min-content size no matter what width
  // says. Measured at 1366px it rendered 864px wide, leaving the text column
  // 270px and wrapping the greeting. That was always broken; it only became
  // visible when audio became the default and videoUrl was normally absent.
  // Let flex do the sizing. The chat column already carries `flex-1`, and it
  // also carried `w-full` and a percentage, three rules for one axis fighting
  // each other. min-w-0 matters too: flex items default to `min-width: auto`,
  // which floors them at min-content, so a pane with content ignores `width`
  // entirely. Measured at 1366px the media pane computed 864px while its class
  // said 30%, squeezing the text to 270px and wrapping the greeting.
  //
  // So: the media pane gets a fixed share and is told not to flex, the chat
  // column takes whatever is left. One rule each.
  // w-1/3 rather than w-[30%] is a readability choice, nothing more. An earlier
  // pass here claimed arbitrary values never reach the page and that this was
  // the bug; that was wrong. Generating the stylesheet directly and reading it
  // shows `.md\:w-\[30\%\]` emitted inside @media (min-width:768px), exactly as
  // it should be. Do not "fix" arbitrary widths elsewhere on that theory.
  //
  // min-w-0 is the rule that actually mattered. Flex items default to
  // min-width:auto, so they floor at min-content and ignore *any* width you set
  // -- 30%, 1/3, or otherwise. That is why the pane measured 1133px while its
  // class said 30%: the width was applying and then losing to the floor.
  //
  // The media pane is never empty -- avatar with video, VoiceVisualizer without
  // -- so it keeps its share either way and the chat column takes the rest.
  // `relative` matters: the VoiceVisualizer sits in an `absolute inset-x-0`
  // wrapper, so without a positioned ancestor it resolves against whatever is
  // further up the tree and centres itself on the whole viewport. Measured at
  // 1366px it drew at x 635-735 while this pane starts at 988, landing the
  // circle on top of the Signup button in the other column.
  const mediaPaneClass = hasUploadPreview
    ? 'relative w-full min-w-0'
    : 'relative w-full md:w-1/3 md:flex-none min-w-0';
  const chatPaneClass = 'min-w-0';
  // Starts signed-out and reads storage after mount. A lazy initialiser still
  // runs during the FIRST render, so this made a visitor who has the companion
  // app render a different tree from the prerendered file, and hydration threw
  // it away. Same rule as access_token, refresh_token, mediaMode and
  // authFromUrl above: the first render cannot depend on anything the
  // prerenderer did not have.
  const [companionStatus, setCompanionStatus] = useState({
    isInstalled: false,
    isRunning: false,
    showUI: false,
    lastChecked: null,
  });
  useEffect(() => {
    try {
      const stored = localStorage.getItem("companionAppInstalled");
      if (stored === "true") {
        setCompanionStatus((prev) => ({ ...prev, isInstalled: true }));
      }
    } catch { /* private mode blocks localStorage */ }
  }, []);
  const [animatingMessageIndex, setAnimatingMessageIndex] = useState(null);

  const [showNotification, setShowNotification] = useState(false);

  // Record the agent from an EFFECT on the value, not at the places that set it.
  //
  // Publishing at two set-sites left the seed behind: this component sets
  // currentAgent again later, so the saved file showed one agent's text and the
  // seed carried an earlier one, and / failed with #425 (text content) on every
  // run instead of the #418 it started with. An effect keyed on the value
  // records whatever is actually on screen when the capture lands.
  //
  // Same correction as heroAgentIndex in Home.js, where publishing inside the
  // state updater reached the file not at all.
  useEffect(() => {
    if (currentAgent) publishPrerenderData('currentAgent', currentAgent);
  }, [currentAgent]);

  const isTextModeRef = useRef(isTextMode);
  const waitingTextRef = useRef(waitingText);
  const thinkingStartTimeRef = useRef(thinkingStartTime);
  const currentThinkingIdRef = useRef(currentThinkingId);
  // Read by applyAgentIdleVideo below, which runs inside agent-loading effects
  // that do not list mediaMode in their deps. Reading the state directly there
  // would see whatever it was when the effect closed over it.
  const mediaModeRef = useRef(mediaMode);

  useEffect(() => {
    isTextModeRef.current = isTextMode;
    waitingTextRef.current = waitingText;
    thinkingStartTimeRef.current = thinkingStartTime;
    currentThinkingIdRef.current = currentThinkingId;
    mediaModeRef.current = mediaMode;
  }, [isTextMode, waitingText, thinkingStartTime, currentThinkingId, mediaMode]);

  /**
   * Record an agent's idle video, and put it on screen only if the visitor is
   * actually in video mode.
   *
   * Two different things were being set together at all three agent-loading
   * sites:
   *
   *   setIdleVideoUrl  the CAPABILITY. Decides whether "Video Mode" is offered
   *                    at all, and what changeMediaMode('video') switches to.
   *                    Always correct to record.
   *   setVideoUrl      what is playing NOW. The media pane checks it BEFORE
   *                    mediaMode:
   *
   *                      {videoUrl ? <video/>
   *                        : (mediaMode === 'audio' || audioUrl) ? <visualizer/>
   *
   * so setting both on load made a video-capable agent start its idle video for
   * a visitor sitting in audio mode, with the selector still reading "Audio
   * Mode" and the voice visualizer never appearing. mediaMode defaults to
   * 'audio' and is otherwise the visitor's saved preference, so this quietly
   * overrode a choice they had made -- or the default they had never left.
   *
   * changeMediaMode already keeps this invariant in both directions: 'audio'
   * clears videoUrl, 'video' restores it from idleVideoUrl. Only the load path
   * broke it.
   */
  const applyAgentIdleVideo = (link) => {
    if (!link) return;
    setIdleVideoUrl(link);
    if (mediaModeRef.current === 'video') setVideoUrl(link);
  };

  console.log(companionStatus, showNotification, "status of comp");

  useEffect(() => {
    if (agentName) {
      // The URL param is a slug: it may be the agent's `slug`, its exact name,
      // or its name with spaces/underscores turned into dashes (AgentLanding
      // links "Talk to X" as /agents/<name with spaces -> dashes>). Matching
      // only `name === agentName` missed all of those, so /agents/greenland-helper
      // and /agents/Jack-Sparrow fell through to the default Hevolve agent.
      const norm = (s) => (s || '').toString().toLowerCase().replace(/[\s_]+/g, '-');
      const target = norm(agentName);
      const matches = (agent) =>
        agent.name === agentName ||
        agent.slug === agentName ||
        norm(agent.slug) === target ||
        norm(agent.name) === target;
      let matchedAgent = allAgents.find(matches);

      if (!matchedAgent) {
        matchedAgent = items.find(matches);
      }

      if (matchedAgent) {
        if (
          !currentAgent ||
          currentAgent.prompt_id !== matchedAgent.prompt_id
        ) {
          if (currentAgent?.prompt_id && messages.length > 0) {
            console.log(
              `💾 Saving ${messages.length} messages for agent: ${currentAgent.name}`,
            );
            saveMessagesToStorage(messages, currentAgent.prompt_id);
          }

          console.log(
            `🧹 Clearing messages for agent switch to: ${matchedAgent.name}`,
          );
          setMessages([]);

          setCurrentAgent(matchedAgent);

          setTimeout(() => {
            const savedMessages = loadMessagesFromStorage(
              matchedAgent.prompt_id,
            );
            console.log(
              `📥 Loading ${savedMessages.length} messages for agent: ${matchedAgent.name}`,
            );
            setMessages(savedMessages);
          }, 100);

          if (matchedAgent.fillers) {
            const idleFiller = matchedAgent.fillers.find(
              (filler) => filler.type === "idle",
            );
            applyAgentIdleVideo(idleFiller?.video_link);
          }
        }
      }
    }
  }, [allAgents, agentName]);

  const handleCreateAgentSubmit = async (agentDatafromApi) => {
    try {
      console.log("1. Agent data received:", agentDatafromApi);

      if (currentAgent?.prompt_id && messages.length > 0) {
        console.log(
          `💾 Saving ${messages.length} messages before creating new agent`,
        );
        saveMessagesToStorage(messages, currentAgent.prompt_id);
      }

      console.log(
        `🧹 Clearing messages for new agent: ${agentDatafromApi.name}`,
      );
      setMessages([]);

      setAllAgents((prevAgents) => {
        const newAgents = [...prevAgents, agentDatafromApi];
        console.log("Updated allAgents:", newAgents.length);
        return newAgents;
      });

      setCurrentAgent(agentDatafromApi);

      if (agentDatafromApi.fillers) {
        const idleFiller = agentDatafromApi.fillers.find(
          (filler) => filler.type === "idle",
        );
        applyAgentIdleVideo(idleFiller?.video_link);
      }

      setShowCreateAgentForm(false);
      const encodedName = encodeURIComponent(agentDatafromApi.name);
      navigate(`/agents/${encodedName}`);

      console.log(
        `✨ New agent created with fresh chat: ${agentDatafromApi.name}`,
      );
    } catch (error) {
      console.error("Error creating agent:", error);
    }
  };

  useEffect(() => {
    return () => {
      // Save messages when component unmounts
      if (currentAgent?.prompt_id && messages.length > 0) {
        console.log(
          `💾 Component unmount: Saving ${messages.length} messages for ${currentAgent.name}`,
        );
        saveMessagesToStorage(messages, currentAgent.prompt_id);
      }
    };
  }, []);

  useEffect(() => {
    if (agentData) {
      setCurrentAgent(agentData);
      if (agentData.fillers) {
        const idleFiller = agentData.fillers.find(
          (filler) => filler.type === "idle",
        );
        applyAgentIdleVideo(idleFiller?.video_link);
      }
    }

    const fetchPrompts = async () => {
      try {
        let allAgents = [];

        // Local-first: try the local backend, fall back to cloud.
        //
        // This was `cloudResult.data || []`, and it ALWAYS produced []. The
        // axios response interceptor (services/axiosFactory.js) already returns
        // `response.data`, so these helpers resolve to the array itself. Reading
        // `.data` off an array is undefined, so the fallback silently collapsed
        // to empty -- while the local call survived only because it was written
        // `localResult.data || localResult`, whose second arm caught the
        // unwrapped case.
        //
        // In production /prompts/public has no Kong route and 404s, so the cloud
        // fallback is the ONLY source of agents; with it returning [], every
        // visitor got "Fetched public agents: 0" and /agents/<any-slug> fell
        // back to the default Hevolve agent.
        //
        // Accept either shape, and treat an empty local result as failure too.
        const asArray = (res) => {
          const d = res && res.data !== undefined ? res.data : res;
          return Array.isArray(d) ? d : [];
        };
        try {
          allAgents = asArray(await chatApi.getPublicPrompts());
        } catch (localErr) {
          allAgents = [];
        }
        if (allAgents.length === 0) {
          try {
            allAgents = asArray(await chatApi.getPublicPromptsCloud());
          } catch (cloudErr) {
            allAgents = [];
          }
        }
        console.log("Fetched public agents:", allAgents.length);

        if (decryptedUserId) {
          try {
            const userResult = await chatApi.getPrompts(decryptedUserId);
            const userData = userResult.data || userResult || [];
            const userAgents = Array.isArray(userData) ? userData : [];
            console.log("Fetched user agents:", userAgents.length);

            userAgents.forEach((userAgent) => {
              const exists = allAgents.some(
                (agent) => agent.prompt_id === userAgent.prompt_id,
              );
              if (!exists) {
                allAgents.push(userAgent);
              }
            });
          } catch (userError) {
            console.warn(
              "Failed to fetch user agents, continuing with public agents only:",
              userError,
            );
          }
        }

        console.log("Total merged agents:", allAgents);
        setAllAgents(allAgents);
      } catch (error) {
        console.error("Error fetching agents:", error);
        setAllAgents([]);
      }
    };
    fetchPrompts();
  }, [agentData, decryptedUserId]);

  useEffect(() => {}, [audioUrl, videoUrl]);

  useEffect(() => {
    const getUrlParams = () => {
      const searchParams = new URLSearchParams(window.location.search);
      const params = {
        agentName: searchParams.get("agent_name")?.trim(),
        token: searchParams.get("token")?.trim(),
        userId: searchParams.get("userid")?.trim(),
        email: searchParams.get("email")?.trim(),
        isTextMode: searchParams.get("text_mode")?.trim() === "true" || false,
      };
      console.log("[Debug]: Extracted URL params:", params);
      return params;
    };

    const encryptValue = (value) => {
      try {
        if (!value || !SECRET_KEY) {
          console.warn("[Encryption Warning]: Missing value or SECRET_KEY");
          return null;
        }
        const encrypted = CryptoJS.AES.encrypt(value, SECRET_KEY).toString();
        console.log("[Encryption]: Value encrypted successfully");
        return encrypted;
      } catch (error) {
        console.error("[Encryption Error]: Failed to encrypt value", error);
        return null;
      }
    };

    const storeEncryptedItem = (key, value) => {
      try {
        const encrypted = encryptValue(value);
        if (encrypted) {
          localStorage.setItem(key, encrypted);
          console.log(`[LocalStorage]: Set ${key} =`, encrypted);
          return true;
        } else {
          console.warn(
            `[LocalStorage Warning]: Encrypted value for ${key} is null`,
          );
        }
      } catch (error) {
        console.error(`[LocalStorage Error]: Failed to store ${key}`, error);
      }
      return false;
    };

    const params = getUrlParams();
    let paramsFound = false;

    if (params.token) {
      try {
        localStorage.setItem("access_token", params.token);
        console.log("[Auth]: Token stored:", params.token);
        paramsFound = true;
      } catch (error) {
        console.error("[Token Error]: Failed to store access token", error);
      }
    } else {
      console.warn("[Auth Warning]: No token found in URL");
    }

    if (params.userId) {
      console.log("[UserID]: Found userId in URL:", params.userId);
      if (storeEncryptedItem("user_id", params.userId)) {
        paramsFound = true;
      }
    } else {
      console.warn("[UserID Warning]: No userId found in URL");
    }

    if (params.email) {
      console.log("[Email]: Found email in URL:", params.email);
      if (storeEncryptedItem("email_address", params.email)) {
        paramsFound = true;
      }
    } else {
      console.warn("[Email Warning]: No email found in URL");
    }
    if (params.isTextMode) {
      setIsTextMode(true);
      console.log("[Text Mode]: Text mode enabled from URL");
    }
    if (paramsFound) {
      setDecryptedUserId(params.userId || "");
      setDecryptedEmail(params.email || "");
      localStorage.setItem("auth_source", "url");
      setAuthFromUrl(true);

      console.log("[Auth Source]: Authentication data set from URL parameters");
    } else {
      console.log("[Init]: No URL parameters found for authentication.");
    }
  }, []);

  useEffect(() => {
    const adjustScale = () => {
      if (containerRef.current) {
        const containerHeight = containerRef.current.clientHeight;
        const targetHeight = containerHeight * 0.95;
        setScale(targetHeight / 842);
      }
    };

    adjustScale();
    window.addEventListener("resize", adjustScale);
    return () => window.removeEventListener("resize", adjustScale);
  }, [scale]);
  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
      setScreenHeight(window.innerHeight);
    };
    // Once on mount, so this browser's real viewport takes over from the
    // prerendered one, and so the prerenderer records what it used.
    publishPrerenderData('viewportHeight', window.innerHeight);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getChatStorageKey = (promptId) => {
    return `chat_messages_${promptId}`;
  };

  const saveMessagesToStorage = (messages, promptId) => {
    if (!promptId) return;

    try {
      const storageKey = getChatStorageKey(promptId);
      const chatData = {
        agentId: promptId,
        agentName: currentAgent?.name,
        lastUpdated: new Date().toISOString(),
        messageCount: messages.length,
        messages: messages,
      };
      localStorage.setItem(storageKey, JSON.stringify(chatData));
      console.log(`💾 Saved ${messages.length} messages for agent ${promptId}`);
    } catch (error) {
      console.error("Failed to save messages to localStorage:", error);
    }
  };

  const loadMessagesFromStorage = (promptId) => {
    if (!promptId) return [];

    try {
      const storageKey = getChatStorageKey(promptId);
      const savedData = localStorage.getItem(storageKey);

      if (savedData) {
        const chatData = JSON.parse(savedData);
        const messages = Array.isArray(chatData)
          ? chatData
          : chatData.messages || [];
        console.log(
          `📥 Loaded ${messages.length} messages for agent ${promptId}`,
        );
        return messages;
      }
    } catch (error) {
      console.error("Failed to load messages from localStorage:", error);
    }

    return [];
  };

  const getVideoWidthforMobile = () => {
    if (screenWidth <= 360) return "70%";
    if (screenWidth <= 450) return "70%";
    if (screenWidth < 500) return "60%";

    if (screenWidth >= 500 && screenWidth <= 650) return "50%";

    if (screenWidth <= 768) return "40%";
    if (screenWidth >= 2001) return 500;
    if (screenWidth >= 1861) return 460;
    if (screenWidth >= 1751) return 440;
    if (screenWidth >= 1651) return 420;
    if (screenWidth >= 1551) return 400;
    if (screenWidth >= 1451) return 380;
    if (screenWidth >= 1351) return 360;
    if (screenWidth >= 1251) return 340;
    if (screenWidth >= 1151) return 320;
    if (screenWidth >= 1051) return 300;
    if (screenWidth >= 951) return 280;
    return 260;
  };

  // Apply per-branch side-effects + persist the new mode.  Single source
  // of truth for media-mode transitions — both toggleTextMode (button
  // cycle) and the Display Mode <select> call through here so the two
  // entry points can never diverge.
  const changeMediaMode = (next) => {
    if (next === 'text') {
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setVideoUrl(null);
      setAudioUrl(null);
      setIsPlayingResponse(false);
    } else if (next === 'audio') {
      // Audio-only: stop the idle video (visualizer becomes the focal
      // surface).  Server-pushed audio still flows in via setAudioUrl.
      if (videoRef.current && videoUrl === idleVideoUrl) {
        videoRef.current.pause();
      }
      setVideoUrl(null);
    } else if (next === 'video') {
      if (idleVideoUrl) {
        setVideoUrl(idleVideoUrl);
      }
    }
    setMediaMode(next);
  };

  /**
   * The modes THIS agent can actually be shown in.
   *
   * Video exists only when the agent carries an idle filler with a video_link:
   * changeMediaMode('video') already guards on idleVideoUrl, so listing it for
   * a text-or-audio agent offers a choice that silently does nothing.
   *
   * One list, rendered by both selects. They had drifted into two different
   * controls for the same state: the mobile menu offered audio/video/text and
   * was bound to mediaMode, while the desktop header offered only
   * "Default Mode (audio only)" and "Text Mode", was UNCONTROLLED, and set the
   * mode through the isTextMode shim instead of changeMediaMode -- so it never
   * paused a playing video, never cleared the media URLs, and could not show a
   * mode chosen anywhere else.
   *
   * Worse, the shim maps false to 'video' (see its definition above), so the
   * option reading "Default Mode (audio only)" put the page into VIDEO mode.
   * The comment on that shim says not to add call sites to it; this was one.
   */
  const mediaModeOptions = [
    { value: 'audio', label: 'Audio Mode' },
    ...(idleVideoUrl ? [{ value: 'video', label: 'Video Mode' }] : []),
    { value: 'text', label: 'Text Mode' },
  ];

  // 3-way cycle: audio → video → text → audio (parity with Nunba's
  // changeMediaMode + toggle).  The button label still says "Text"
  // for back-compat with existing UI references; future PR can
  // rename to "Mode" and add a 3-state segmented control.
  const toggleTextMode = () => {
    const cycle = { audio: 'video', video: 'text', text: 'audio' };
    changeMediaMode(cycle[mediaMode] || 'audio');
  };

  const handleCreateAgentClick = () => {
    if (!decryptedUserId || !token) {
      setIsModalOpen(true);
      return;
    }
    setShowCreateAgentForm(true);
  };
  const handleAgentSelect = (agent) => {
    setShowAgentsOverlay(false);
    console.log("hello");

    if (agent && agent.prompt_id !== currentAgent?.prompt_id) {
      if (currentAgent?.prompt_id && messages.length > 0) {
        console.log(
          `💾 Saving ${messages.length} messages before selecting ${agent.name}`,
        );
        saveMessagesToStorage(messages, currentAgent.prompt_id);
      }

      console.log(`🧹 Clearing messages for agent selection: ${agent.name}`);
      setMessages([]);

      setTimeout(() => {
        handleButtonClick(agent);
      }, 50);
    }
  };

  const safeParsePayload = (data) => {
    if (data && typeof data === "object" && !Array.isArray(data)) {
      return data;
    }

    if (Array.isArray(data)) {
      return data[0] && typeof data[0] === "object" ? data[0] : data;
    }

    if (typeof data !== "string") {
      return data || {};
    }

    try {
      return JSON.parse(data);
    } catch {}

    try {
      let jsonString = data;

      const protectedStrings = [];
      jsonString = jsonString.replace(/(\w)'(\w)/g, (match, p1, p2) => {
        const placeholder = `__APOSTROPHE_${protectedStrings.length}__`;
        protectedStrings.push({ placeholder, p1, p2 });
        return placeholder;
      });

      jsonString = jsonString.replace(/\bNone\b/g, "null");
      jsonString = jsonString.replace(/\bTrue\b/g, "true");
      jsonString = jsonString.replace(/\bFalse\b/g, "false");
      jsonString = jsonString.replace(/'/g, '"');

      protectedStrings.forEach(({ placeholder, p1, p2 }) => {
        jsonString = jsonString.replace(placeholder, `${p1}'${p2}`);
      });

      return JSON.parse(jsonString);
    } catch (err) {
      console.error("❌ Failed to parse payload:", err.message);
      return { error: "parse_failed", raw: data };
    }
  };

  const handleDataReceived = useCallback((data) => {
    console.log("🚦 handleDataReceived START");
    console.log("📥 Raw data:", data);

    const getCurrentState = () => ({
      isTextMode: isTextModeRef.current,
      waitingText: waitingTextRef.current,
      thinkingStartTime: thinkingStartTimeRef.current,
      currentThinkingId: currentThinkingIdRef.current,
    });

    try {
      let rawPayload = data;

      if (data && typeof data === "object" && data.data !== undefined) {
        rawPayload = data.data;
      }

      const parsed = safeParsePayload(rawPayload);

      if (parsed?.error === "parse_failed") {
        console.error("❌ Payload parsing failed, aborting message handling");
        setLoading(false);
        return;
      }

      // A background agent's turn is not part of this conversation. Its
      // thinking would be filed under the person's last message, and its
      // status text would fall through below as an assistant reply and close
      // the person's own thinking container. Dropped before any state is
      // touched, including the loading spinner, which belongs to their turn.
      if (isBackgroundRequest(parsed?.request_id)) {
        return;
      }

      const { isTextMode, waitingText, thinkingStartTime, currentThinkingId } =
        getCurrentState();

      if (Number(parsed.priority) === 49 && parsed.action === "Thinking") {
        console.log("🧠 THINKING MODE DETECTED");

        const thinkingText = parsed.text?.[0] || "";
        const requestId = parsed.request_id || "unknown";

        const uniqueThinkingId = `thinking_step_${Date.now()}_${Math.random()
          .toString(36)
          .substr(2, 9)}`;

        if (!thinkingStartTime) {
          setThinkingStartTime(new Date());
        }

        let containerRequestId = null;
        let userMessageIndex = null;

        setMessages((prev) => {
          const lastUserMessageIndex = prev
            .map((msg, index) => ({ msg, index }))
            .reverse()
            .find(({ msg }) => msg.type === "user")?.index;

          if (lastUserMessageIndex === undefined) {
            console.warn("No user message found for thinking step");
            return prev;
          }

          containerRequestId = `${requestId}_after_user_${lastUserMessageIndex}`;
          userMessageIndex = lastUserMessageIndex;

          const existingContainerIndex = prev.findIndex(
            (msg, index) =>
              msg.type === "thinking_container" &&
              msg.containerRequestId === containerRequestId &&
              index > lastUserMessageIndex,
          );

          if (existingContainerIndex >= 0) {
            const updated = [...prev];
            const newThinkingStep = {
              id: uniqueThinkingId,
              content: thinkingText,
              timestamp: new Date(),
              lastUpdated: new Date(),
              isExpanded: false,
              isCompleted: false,
              duration: null,
            };

            updated[existingContainerIndex] = {
              ...updated[existingContainerIndex],
              thinkingSteps: [
                ...updated[existingContainerIndex].thinkingSteps,
                newThinkingStep,
              ],
              lastUpdated: new Date(),
            };

            return updated;
          } else {
            const newThinkingContainer = {
              type: "thinking_container",
              id: `thinking_container_${containerRequestId}`,
              requestId: requestId,
              containerRequestId: containerRequestId,
              userMessageIndex: lastUserMessageIndex,
              timestamp: new Date(),
              lastUpdated: new Date(),
              isMainExpanded: false,
              isCompleted: false,
              totalDuration: null,
              thinkingSteps: [
                {
                  id: uniqueThinkingId,
                  content: thinkingText,
                  timestamp: new Date(),
                  lastUpdated: new Date(),
                  isExpanded: false,
                  isCompleted: false,
                  duration: null,
                },
              ],
            };

            return [...prev, newThinkingContainer];
          }
        });

        setCurrentThinkingId(containerRequestId);
        setLoading(false);
        return;
      }

      const extractedText = parsed.text?.[0] || "";
      const responseVideoUrl = parsed.video_link?.video || "";
      const responseAudioUrl = parsed.video_link?.aud_url || "";

      // Draft-replacement: if the WAMP message carries a speculation_id,
      // find the draft bubble and replace its content in-place instead of
      // appending a second message. This prevents the "two bubbles" confusion.
      if (parsed.speculation_id && extractedText && !parsed.expert_pending) {
        const specId = parsed.speculation_id;
        setMessages((prev) => {
          const idx = prev.findIndex(
            (m) => m.speculationId === specId && m.isDraft,
          );
          if (idx !== -1) {
            const updated = [...prev];
            updated[idx] = {
              ...updated[idx],
              content: extractedText,
              isDraft: false,
              source: parsed.source || "expert",
            };
            return updated;
          }
          // No matching draft found — append normally
          return [
            ...prev,
            {
              type: "assistant",
              content: extractedText,
              source: parsed.source || "expert",
            },
          ];
        });
        setShouldScroll(true);
        setLoading(false);
        return;
      }

      if (extractedText || responseVideoUrl || responseAudioUrl) {
        const endTime = new Date();
        const duration = thinkingStartTime
          ? (endTime - thinkingStartTime) / 1000
          : 0;

        console.log("🏁 MAIN RESPONSE ARRIVED");
        console.log("🔍 Current thinking ID:", currentThinkingId);
        console.log("🔍 Response request_id:", parsed.request_id);
        console.log("🔍 Duration calculated:", duration);

        let foundAndCompleted = false;

        setMessages((prev) => {
          console.log("📋 Looking for thinking containers to complete...");

          const thinkingContainers = prev.filter(
            (msg) => msg.type === "thinking_container",
          );
          console.log(
            "🔍 Found thinking containers:",
            thinkingContainers.length,
          );

          const updated = prev.map((msg) => {
            if (msg.type === "thinking_container" && !msg.isCompleted) {
              if (
                currentThinkingId &&
                msg.containerRequestId === currentThinkingId
              ) {
                foundAndCompleted = true;
                return {
                  ...msg,
                  isCompleted: true,
                  totalDuration: duration,
                  completedAt: endTime,
                  thinkingSteps: msg.thinkingSteps.map((step) => ({
                    ...step,
                    isCompleted: true,
                    duration: duration / msg.thinkingSteps.length,
                  })),
                };
              }

              if (
                parsed.request_id &&
                (msg.requestId === parsed.request_id ||
                  msg.containerRequestId?.includes(parsed.request_id))
              ) {
                foundAndCompleted = true;
                return {
                  ...msg,
                  isCompleted: true,
                  totalDuration: duration,
                  completedAt: endTime,
                  thinkingSteps: msg.thinkingSteps.map((step) => ({
                    ...step,
                    isCompleted: true,
                    duration: duration / msg.thinkingSteps.length,
                  })),
                };
              }
            }
            return msg;
          });

          if (!foundAndCompleted && thinkingStartTime) {
            for (let i = updated.length - 1; i >= 0; i--) {
              if (
                updated[i].type === "thinking_container" &&
                !updated[i].isCompleted
              ) {
                updated[i] = {
                  ...updated[i],
                  isCompleted: true,
                  totalDuration: duration,
                  completedAt: endTime,
                  thinkingSteps: updated[i].thinkingSteps.map((step) => ({
                    ...step,
                    isCompleted: true,
                    duration: duration / updated[i].thinkingSteps.length,
                  })),
                };
                break;
              }
            }
          }

          return updated;
        });

        setCurrentThinkingId(null);
        setThinkingStartTime(null);
      }

      if (isTextMode) {
        if (extractedText) {
          const assistantMessage = {
            type: "assistant",
            content: extractedText,
            // Draft-first: tag with speculationId so the expert
            // response arriving via WAMP can replace this bubble
            // instead of appending a second one.
            ...(parsed.speculation_id &&
              parsed.expert_pending && {
                speculationId: parsed.speculation_id,
                isDraft: true,
              }),
          };
          setMessages((prev) => {
            const newMessages = [...prev, assistantMessage];
            setAnimatingMessageIndex(null);
            setShouldScroll(true);
            return newMessages;
          });
          setLoading(false);
        }

        if (parsed.page_image_url) {
          setUploadedImage(parsed.page_image_url);
        }
        return;
      }

      const hasText = !!extractedText;
      const hasAudio = !!(responseAudioUrl || responseVideoUrl);
      const hasWaitingText = !!waitingText;

      if (hasText && !hasAudio) {
        const assistantMessage = {
          type: "assistant",
          content: extractedText,
          // Draft-first: tag with speculationId so the expert
          // response arriving via WAMP can replace this bubble
          // instead of appending a second one.
          ...(parsed.speculation_id &&
            parsed.expert_pending && {
              speculationId: parsed.speculation_id,
              isDraft: true,
            }),
        };

        setMessages((prev) => {
          const newMessages = [...prev, assistantMessage];
          setAnimatingMessageIndex(1);
          setShouldScroll(true);
          return newMessages;
        });

        setWaitingText(null);
        setLoading(false);
        return;
      }
      if (hasAudio) {
        let textToShow = hasWaitingText
          ? waitingText
          : hasText
            ? extractedText
            : null;

        if (textToShow) {
          const assistantMessage = {
            type: "assistant",
            content: textToShow,
            // Draft-first: tag with speculationId so the expert
            // response arriving via WAMP can replace this bubble
            // instead of appending a second one.
            ...(parsed.speculation_id &&
              parsed.expert_pending && {
                speculationId: parsed.speculation_id,
                isDraft: true,
              }),
          };
          setMessages((prev) => {
            const newMessages = [...prev, assistantMessage];
            setAnimatingMessageIndex(newMessages.length - 1);
            setShouldScroll(true);
            return newMessages;
          });
          setWaitingText(null);
        }

        setVideoUrl(responseVideoUrl);
        setAudioUrl(responseAudioUrl);
        setIsPlayingResponse(true);
        setLoading(false);
      }

      if (parsed.page_image_url) {
        setUploadedImage(parsed.page_image_url);
      }
    } catch (err) {
      console.error("❌ Error processing data:", err);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    console.log("🎯 currentThinkingId changed:", currentThinkingId);
  }, [currentThinkingId]);

  useEffect(() => {
    console.log("📝 thinkingMessages1 changed:", messages);
  }, [messages]);

  useEffect(() => {
    let payload = {
      agentname: currentAgent?.name,
      email: decryptedEmail,
      access_token: token,
      user_id: decryptedUserId,
    };
    if (companionStatus.isRunning) {
      const sendPostRequest = async () => {
        try {
          const response = await axios.post(
            "http://localhost:5000/api/storage/set",
            payload,
          ); //this is for setting the value in companion app

          console.log("POST request response:", response.data);
        } catch (error) {
          console.error("Failed to send POST request:", error);
        }
      };

      sendPostRequest();
    }
  }, [
    companionStatus.isRunning,
    currentAgent?.name,
    decryptedEmail,
    token,
    decryptedUserId,
  ]);

  useEffect(() => {
    let activeWorker = null;
    let isInitializing = false;

    const initializeWorker = () => {
      if (isInitializing) {
        console.log("⚠️ Worker initialization already in progress");
        return;
      }

      isInitializing = true;

      try {
        if (activeWorker) {
          console.log("🔄 Terminating existing worker");
          activeWorker.terminate();
          activeWorker = null;
        }

        const crossbarWorker = new Worker(
          new URL("./crossbarWorker.js", import.meta.url),
          { type: "module" },
        );

        crossbarWorker.onmessage = (e) => {
          const { type, payload } = e.data;

          switch (type) {
            case "CONNECTION_STATUS":
              setConnectionStatus(payload);
              break;

            case "PROGRESS_UPDATE":
              setProgress(payload);
              break;

            case "DATA_RECEIVED":
              handleDataReceived(payload);
              break;

            case "LOG":
              break;

            case "ERROR":
              console.error(`❌ Error:`, payload);
              break;

            case "COMPANION_STATUS_UPDATE":
              const storedInstallationStatus =
                localStorage.getItem("companionAppInstalled") === "true";

              if (payload.isInstalled && !storedInstallationStatus) {
                localStorage.setItem("companionAppInstalled", "true");
              }

              setCompanionStatus((prevStatus) => {
                const finalInstallationStatus =
                  storedInstallationStatus || payload.isInstalled;

                const newStatus = {
                  isInstalled: finalInstallationStatus,
                  isRunning: payload.isRunning,
                  showUI: payload.showUI === true,
                  lastChecked: new Date().toISOString(),
                };

                if (payload.showUI && payload.fromActionRequest) {
                  setShowNotification(true);
                  setTimeout(() => {
                    setShowNotification(false);
                  }, 6000);
                }

                return newStatus;
              });
              break;

            case "UPDATE_LOCAL_STORAGE":
              localStorage.setItem(payload.key, payload.value);
              break;

            default:
              break;
          }
        };

        crossbarWorker.onerror = (error) => {
          console.error("❌ Worker error:", error);
          setConnectionStatus("Worker Error");
          isInitializing = false;
        };

        activeWorker = crossbarWorker;
        setWorker(crossbarWorker);
        initGameRealtime(crossbarWorker);
        realtimeService.init(crossbarWorker);

        if (decryptedUserId) {
          console.log(
            "🚀 Initializing worker with user ID:",
            decryptedUserId,
            requestId,
          );
          crossbarWorker.postMessage({
            type: "INIT",
            payload: {
              wsUri: "wss://aws_rasa.hertzai.com:8445/wss",
              userId: decryptedUserId,
              maxRetries: 8,
              retryDelay: 5000,
            },
          });

          // Provide JWT for SSE fallback (used when crossbar is unreachable)
          const token = localStorage.getItem("access_token");
          if (token) realtimeService.connect(token);
        }
      } catch (error) {
        console.error("❌ Worker initialization error:", error);
        setConnectionStatus("Failed to Initialize");
      } finally {
        isInitializing = false;
      }
    };

    if (decryptedUserId && !activeWorker) {
      initializeWorker();
    }

    return () => {
      console.log("🧹 Cleanup: Terminating worker");
      realtimeService.disconnect();
      if (activeWorker) {
        activeWorker.terminate();
      }
      isInitializing = false;
    };
  }, [decryptedUserId]);

  useEffect(() => {
    if (requestId && worker) {
      console.log("📋 Sending request ID to worker:", requestId);
      setRequestIdFromCrossbar(requestId);
      worker.postMessage({
        type: "SET_REQUEST_ID",
        payload: {
          request_Id: requestId,
        },
      });
    }
  }, [requestId, worker]);
  useEffect(() => {
    const decryptValue = (encryptedValue) => {
      try {
        const bytes = CryptoJS.AES.decrypt(encryptedValue, SECRET_KEY);
        const originalValue = bytes.toString(CryptoJS.enc.Utf8);
        return originalValue;
      } catch (error) {
        console.error("Failed to decrypt value:", error);
        return null;
      }
    };

    const encryptedUserId = localStorage.getItem("user_id");
    const encryptedEmail = localStorage.getItem("email_address");

    if (encryptedUserId && encryptedEmail) {
      const userId = decryptValue(encryptedUserId);
      const email = decryptValue(encryptedEmail);

      setDecryptedUserId(userId);
      setDecryptedEmail(email);
    } else {
      console.warn("No userId or email found in localStorage.");
    }
  }, [decryptedEmail, decryptedUserId]);
  const isIdleVideo = (videoUrl) => {
    return videoUrl === idleVideoUrl;
  };

  const handleMediaEnded = () => {
    try {
      console.log("🎬 Media ended, returning to idle video");
      console.log("🔊 Current audioUrl:", audioUrl);
      console.log("🎥 Current videoUrl:", videoUrl);

      setIsPlayingResponse(false);
      setAnimatingMessageIndex(null);
      setAudioUrl(null); // This should clear the audio URL

      // Get idle video URL
      let targetIdleUrl = idleVideoUrl;
      if (!targetIdleUrl) {
        const idleVideo =
          currentAgent?.fillers?.find((filler) => filler.type === "idle") ||
          agentData?.fillers?.find((filler) => filler.type === "idle");

        if (idleVideo?.video_link) {
          targetIdleUrl = idleVideo.video_link;
          setIdleVideoUrl(targetIdleUrl);
        }
      }

      if (targetIdleUrl) {
        console.log("🎥 Setting idle video:", targetIdleUrl);
        setTimeout(() => {
          setVideoUrl(targetIdleUrl);

          if (videoRef.current) {
            videoRef.current.src = targetIdleUrl;
            videoRef.current.load();

            videoRef.current.addEventListener(
              "canplay",
              () => {
                videoRef.current
                  .play()
                  .catch((err) => console.warn("Idle video play failed:", err));
              },
              { once: true },
            );
          }
        }, 200);
      } else {
        console.warn("Idle video not found in agent fillers.");
      }
    } catch (error) {
      console.error("Error handling media end:", error);
    }
  };

  const handleButtonClick = (chat) => {
    setVideoUrl(null);
    setCurrentAgent(chat);
    console.log("hello");
    if (chat && chat.prompt_id !== currentAgent?.prompt_id) {
      //for save messsage
      if (currentAgent?.prompt_id && messages.length > 0) {
        console.log(
          `💾 Saving ${messages.length} messages before switching to ${chat.name}`,
        );
        saveMessagesToStorage(messages, currentAgent.prompt_id);
      }

      console.log(`🧹 Clearing messages for agent switch to: ${chat.name}`);
      setMessages([]);

      const idleFiller = chat.fillers?.find((filler) => filler.type === "idle");
      setVideoUrl(idleFiller?.video_link);
      setCurrentAgent(chat);

      setTimeout(() => {
        const savedMessages = loadMessagesFromStorage(chat.prompt_id);
        console.log(
          `📥 Loading ${savedMessages.length} messages for agent: ${chat.name}`,
        );
        setMessages(savedMessages);
      }, 100);

      setIsOpen(false);
      navigate(`/agents/${encodeURIComponent(chat.name)}`);
    } else {
      console.log("Same agent or agent not found");
    }
  };

  useEffect(() => {
    if (shouldScroll) {
      setTimeout(() => {
        scrollToBottom();
        setShouldScroll(false);
      }, 50);
    }
  }, [messages, shouldScroll]);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }
  };

  const handlePlay = async () => {
    if (isTextMode) {
      console.log("Text mode is enabled, skipping video playback");
      return;
    }

    if (videoRef.current) {
      try {
        if (videoRef.current.readyState >= 2) {
          await videoRef.current.play();
        } else {
          videoRef.current.addEventListener(
            "canplay",
            async () => {
              try {
                await videoRef.current.play();
              } catch (err) {
                console.error("Video play failed after canplay:", err);
              }
            },
            { once: true },
          );
        }
      } catch (error) {
        console.error("Video playback failed:", error);
      }
    }
  };

  const TextModeToggle = () => (
    <button
      onClick={toggleTextMode}
      className={`flex items-center gap-2 px-3 py-1 rounded-lg transition-colors ${
        isTextMode
          ? "bg-blue-600 text-white"
          : "bg-gray-200 text-gray-700 hover:bg-gray-300"
      }`}
      title={isTextMode ? "Disable Text Mode" : "Enable Text Mode"}
    >
      <FileText className="w-4 h-4" />
      <span className="text-sm">{isTextMode ? "Text Mode" : "Media Mode"}</span>
    </button>
  );

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setCurrentPage(1);
  };

  const nextPage = () =>
    setCurrentPage((prev) => (prev < numPages ? prev + 1 : prev));
  const prevPage = () => setCurrentPage((prev) => (prev > 1 ? prev - 1 : prev));

  const handleStart = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert("Speech Recognition is not supported in your browser.");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    recognition.onresult = (event) => {
      let transcript = "";
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        transcript += event.results[i][0].transcript;
      }
      setInputMessage((prev) => prev + transcript);
    };

    recognition.onerror = (event) => {
      console.error("Speech recognition error", event.error);
    };

    recognition.onend = () => {
      setIsRecording(false);
    };

    recognition.start();
    recognitionRef.current = recognition;
    setIsRecording(true);
  };

  const handleStop = () => {
    recognitionRef.current?.stop();
    setIsRecording(false);
  };

  const handleLoadedMetadata = (event) => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      handlePlay();
    }
  };
  const handleLoadedMetadataaudio = (event) => {
    const duration = event.target.duration;
    setDuration(duration);
  };
  const handlePdfSelect = async (event) => {
    const file = event.target.files[0];

    if (!file) {
      console.error("No file selected.");
      return;
    }
    if (file && file.type === "application/pdf") {
      const previewUrl = URL.createObjectURL(file);
      setPdfurl(previewUrl);
    }

    try {
      // Set the selected PDF file
      setPdfFile(file);

      var formdata = new FormData();
      formdata.append("bot_type", "book_parsing");
      formdata.append("user_id", decryptedUserId);
      formdata.append("request_id", uuidv4());
      formdata.append("file", file, file.name);

      var requestOptions = {
        method: "POST",
        body: formdata,
        redirect: "follow",
      };

      fetch(configData.bookParsingUpload, requestOptions)
        .then(async (response) => {
          if (!response.ok) {
            console.error(
              "Failed to upload PDF:",
              response.status,
              response.statusText,
            );
            return;
          }

          const result = await response.json();
          setRequestId(result.request_id);

          setpdfFileUrl(result.file_url);
        })
        .catch((error) => {
          console.error("Error during PDF upload process:", error);
        });
    } catch (error) {
      console.error("Error during PDF upload process:", error);
    }
  };

  const handleRemovePdf = () => {
    setPdfFile(null);
    setpdfFileUrl(null);
  };

  const handleImageSelect = async (event) => {
    const file = event.target.files[0];

    if (file) {
      setIsImageUploading(true);
      setUserImage(URL.createObjectURL(file));

      const formData = new FormData();
      formData.append("user_id", decryptedUserId);
      formData.append("file", file, file.name);
      formData.append("request_id", uuidv4());

      try {
        const response = await fetch(configData.uploadFile, {
          method: "POST",
          body: formData,
          redirect: "follow",
        });

        if (response.ok) {
          const result = await response.json();
          setUserImage(result.file_url);
        } else {
          console.error(
            "Failed to upload image:",
            response.status,
            response.statusText,
          );
        }
      } catch (error) {
        console.error("Error uploading image:", error);
      } finally {
        setIsImageUploading(false);
      }
    }
  };

  const handleRemoveImage = () => {
    setUserImage(null);
    setSelectedFile(null);
    setFileUrl(null);
  };

  const LogOutUser = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("user_id");
    localStorage.removeItem("email_address");
    navigate("/");
  };

  const handleFocus = () => {
    if (!token) {
      setShowTooltip(true);
    }
  };

  const handleBlur = () => {
    setShowTooltip(false);
  };

  const handleSend = async () => {
    const origin = window.location.origin.toLowerCase();
    const pathname = window.location.pathname.toLowerCase();

    console.log(origin, pathname, "origin and pathname");

    const generatedRequestId = requestId || uuidv4();
    setRequestId(generatedRequestId);

    setCurrentThinkingId(null);
    setThinkingStartTime(null);

    const isPersonalisedEndpoint =
      (origin === "https://hertzai.com" &&
        (pathname === "/" || pathname === "")) ||
      pathname.includes("hevolve") ||
      pathname.includes("personalised");

    console.log(isPersonalisedEndpoint, "isPersonalisedEndpoint");

    if (!inputMessage.trim() && !fileUrl && !userImage) return;

    const userMessage = {
      type: "user",
      content: inputMessage,
      pdf: pdffileUrl || null,
      image: userImage || null,
    };

    setMessages((prevMessages) => [...prevMessages, userMessage]);
    setLoading(true);
    setShouldScroll(true);
    setWaitingText(null);
    console.log("agentdata", agentData);
    console.log("currentagent", currentAgent);

    const dataToSend = JSON.stringify({
      text: inputMessage,
      user_id: decryptedUserId,
      teacher_avatar_id: agentData?.teacher_avatar_id || null,
      conversation_id: uuidv4(),
      request_id: generatedRequestId,
      prompt_id: isPersonalisedEndpoint
        ? null
        : currentAgent?.prompt_id || null,
      bot_type: currentAgent?.name || "",
    });

    // Clear form data
    setUploadedPdf(null);
    setUserImage(null);
    setInputMessage("");
    setFileUrl(null);
    setPdfFile(null);
    setpdfFileUrl(null);

    if (textareaRef.current) {
      textareaRef.current.style.height = "70px";
      textareaRef.current.style.overflowY = "hidden";
    }

    try {
      if (!token) {
        console.error("Authorization token is missing.");
        setLoading(false);
        setIsModalOpen(true);
        return;
      }

      const endpoint = isPersonalisedEndpoint
        ? configData.personalisedLearningAgent
        : configData.forAllAgents;

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: dataToSend,
      });

      if (!response.ok) {
        const errorResponse = await response.json();
        console.error("API call failed:", response.status, response.statusText);

        if (response.status === 401) {
          if (!refresh_token) {
            console.warn("Session expired. No refresh token available.");
            setSessionExpiredMessage("Session expired. Please log in again.");
            setIsModalOpen(true);
            setLoading(false);
            return;
          }
        }

        if (
          errorResponse.error === "invalid_token" ||
          errorResponse.error_description ===
            "The access token is invalid or has expired"
        ) {
          console.warn(
            "Access token expired. Removing stored data and opening login modal...",
          );
          localStorage.removeItem("expire_token");
          localStorage.removeItem("access_token");
          localStorage.removeItem("user_id");
          localStorage.removeItem("email_address");
          setIsModalOpen(true);
          setLoading(false);
        }
        return;
      }

      const dataJson = await response.json();

      if (dataJson.status === "no_content") {
        console.log("No content from API. Awaiting Crossbar message...");
        return;
      }

      console.log("✅ API Response received successfully");
      console.log("📋 Request ID:", dataJson.request_id);
      console.log(
        "🔄 Response will be handled by WebSocket/Crossbar via handleDataReceived",
      );

      setUserImage(null);
    } catch (err) {
      console.error("Unexpected error during request:", err);
      setLoading(false);
    }

    setPdfFile(null);
  };

  const handleImageClick = (imageUrl) => {
    setUploadedImage(imageUrl);
  };
  const handlePdfClick = (pdfUrl) => {
    console.log(pdfUrl);
    setUploadedPdf(pdfurl);
  };

  const closePreview = () => {
    setUploadedImage(null);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };
  const items = [
    {
      prompt: "Your name is Radha, you are a sweet beautiful female .",
      prompt_id: 54,
      name: "Personalised Learning",
      created_date: "2024-11-19T10:24:38",
      request_id: "8b3e7d91-a49b-497d-8051-a3fa4ff3c53e",
      is_public: true,
      create_agent: false,
      is_active: true,
      user_id: 10077,
      image_url:
        "http://aws_rasa.hertzai.com:5459/output/25dfe16e-a6a4-11ef-a097-42010aa00006.png",
      teacher_avatar_id: 2759,
      video_url: null,
      video_text: "This is Static Description",
      teacher_image_url:
        "https://azurekong.hertzai.com/mkt-aws/txt/voice_dump/8f4c3958-9cropped_image.png",
      description:
        "https://azurekong.hertzai.com/mkt-azure/examples/74eaec428f4c3958-9cropped_image_pred_fls_f4203dae_bf375f78-eLily_audio_embed.mp4",
      image_name: "8f4c3958-9cropped_image.png",
      fillers: [
        {
          text: "Oops something went wrong",
          video_link:
            "https://azurekong.hertzai.com/mkt-azure/examples/7293f99b8f4c3958-9cropped_image_pred_fls_289940eb_bf375f78-eLily_audio_embed.mp4",
          type: "internal_server_error",
        },
        {
          text: "",
          video_link:
            "https://azurekong.hertzai.com/mkt-aws/examples/8f4c3958-9cropped_image_pred_fls_Blank_audio_embed.mp4",
          type: "idle",
        },
      ],
    },

    {
      prompt_id: 49,
      name: "Speech Therapy Agent",
      prompt:
        "You are the great India itself,  you talk about greatness of India and Indiana and its diversity. ",
      created_date: "2024-11-11T10:06:25",
      request_id: "9ac457f0-eb8d-4869-9d24-4d445f6f6d66",
      is_public: true,
      create_agent: false,
      is_active: true,
      user_id: decryptedUserId,
      image_url:
        "http://aws_rasa.hertzai.com:5459/output/32cbfdba-a17a-11ef-b355-42010aa00006.png",
      teacher_avatar_id: 2739,
      video_url: null,
      video_text: "This is Static Description",
      teacher_image_url:
        "https://azurekong.hertzai.com/mkt-aws/txt/voice_dump/acb7ea45-0cropped_image.png",
      description:
        "https://azurekong.hertzai.com/mkt-azure/examples/ed5b3666acb7ea45-0cropped_image_pred_fls_a94bd0b3_2665af70-2Katie_audio_embed.mp4",
      image_name: "acb7ea45-0cropped_image.png",
      fillers: [
        {
          text: "Oops something went wrong",
          video_link:
            "https://azurekong.hertzai.com/mkt-aws/examples/f6a8d897acb7ea45-0cropped_image_pred_fls_b4e1970c_2665af70-2Katie_audio_embed.mp4",
          type: "internal_server_error",
        },
        {
          text: "",
          video_link:
            "https://azurekong.hertzai.com/mkt-aws/examples/acb7ea45-0cropped_image_pred_fls_Blank_audio_embed.mp4",
          type: "idle",
        },
      ],
    },

    {
      prompt_id: 20,
      name: "Spoken English Agent",
      prompt: "talk as if you are my kid",
      created_date: "2024-07-10T15:21:01",
      request_id: "1c11d245-08f8-4e1a-9d2d-5c8128a0b185",
      is_public: true,
      create_agent: false,
      is_active: true,
      user_id: 10077,
      image_url:
        "http://aws_rasa.hertzai.com:5459/output/cb7b4fe8-3ed8-11ef-becc-000d3af074c1.png",
      teacher_avatar_id: 2193,
      video_url: null,
      video_text: "This is Static Description",
      teacher_image_url:
        "https://azurekong.hertzai.com/mkt-aws/txt/voice_dump/comfyinstant_89b6fbf1-2cropped2922730214132889043.png",
      description:
        "https://azurekong.hertzai.com/mkt-azure/examples/0b63bbb0comfyinstant_89b6fbf1-2cropped2922730214132889043_pred_fls_cc94d287_450f5ce5-3Margaret_audio_embed.mp4",
      image_name: "comfyinstant_89b6fbf1-2cropped2922730214132889043.png",
      fillers: [
        {
          text: "Oops something went wrong",
          video_link:
            "https://azurekong.hertzai.com/mkt-azure/examples/5fffd4e3comfyinstant_89b6fbf1-2cropped2922730214132889043_pred_fls_4ad5022f_450f5ce5-3Margaret_audio_embed.mp4",
          type: "internal_server_error",
        },
        {
          text: "",
          video_link:
            "https://azurekong.hertzai.com/mkt-azure/examples/comfyinstant_89b6fbf1-2cropped2922730214132889043_pred_fls_Blank_audio_embed.mp4",
          type: "idle",
        },
      ],
    },

    {
      prompt_id: 5,
      name: "News Agent",
      prompt: "You are a Teacher who responds as if you are Naruto",
      created_date: "2024-02-08T10:46:49",
      request_id: null,
      is_public: true,
      create_agent: false,
      is_active: true,
      user_id: 104,
      image_url:
        "https://azurekong.hertzai.com/mkt-azure/examples/bace4e36-0naruto.jpg",
      teacher_avatar_id: 2759,
      video_url:
        "https://azurekong.hertzai.com/mkt-azure/examples/74eaec428f4c3958-9cropped_image_pred_fls_f4203dae_bf375f78-eLily_audio_embed.mp4",
      video_text: "This is Static Description",
      teacher_image_url:
        "https://azurekong.hertzai.com/mkt-aws/txt/voice_dump/8f4c3958-9cropped_image.png",
      description:
        "https://azurekong.hertzai.com/mkt-azure/examples/74eaec428f4c3958-9cropped_image_pred_fls_f4203dae_bf375f78-eLily_audio_embed.mp4",
      image_name: "8f4c3958-9cropped_image.png",
      fillers: [
        {
          text: "Oops something went wrong",
          video_link:
            "https://azurekong.hertzai.com/mkt-azure/examples/7293f99b8f4c3958-9cropped_image_pred_fls_289940eb_bf375f78-eLily_audio_embed.mp4",
          type: "internal_server_error",
        },
        {
          text: "",
          video_link:
            "https://azurekong.hertzai.com/mkt-aws/examples/8f4c3958-9cropped_image_pred_fls_Blank_audio_embed.mp4",
          type: "idle",
        },
      ],
    },

    {
      prompt_id: 12,
      name: "Podcast Agent",
      prompt: "create an alaram after an hour",
      created_date: "2024-07-09T01:34:29",
      request_id: "454dfsdfsdsfasf564545",
      is_public: true,
      create_agent: false,
      is_active: true,
      user_id: 10669,
      image_url:
        "http://aws_rasa.hertzai.com:5459/output/8e891f44-96b6-11ef-a097-42010aa00006.png",
      teacher_avatar_id: 1802,
      video_url: null,
      video_text: "This is Static Description",
      teacher_image_url:
        "https://azurekong.hertzai.com/mkt-aws/txt/voice_dump/27b62c6e-fcropped6488793763494973038.png",
      description:
        "https://azurekong.hertzai.com/mkt-azure/examples/77047ca627b62c6e-fcropped6488793763494973038_pred_fls_de571ca5_79b9078f-5Hindi_F_Tyagi_audio_embed.mp4",
      image_name: "27b62c6e-fcropped6488793763494973038.png",
      fillers: [
        {
          text: "",
          video_link:
            "https://azurekong.hertzai.com/mkt-azure/examples/27b62c6e-fcropped6488793763494973038_pred_fls_Blank_audio_embed.mp4",
          type: "idle",
        },
      ],
    },

    {
      prompt_id: 13,
      name: "Story Narrator Agent",
      prompt: "play music for 6 month old",
      created_date: "2024-07-09T10:23:12",
      request_id: "14417881-e4d8-467d-8201-b66552e48531",
      is_public: true,
      create_agent: false,
      is_active: true,
      user_id: 10676,
      image_url:
        "http://aws_rasa.hertzai.com:5459/output/4b987996-96bb-11ef-a35b-42010aa00006.png",
      teacher_avatar_id: 2141,
      video_url: null,
      video_text: "This is Static Description",
      teacher_image_url:
        "https://azurekong.hertzai.com/mkt-aws/txt/voice_dump/comfyinstant_235bda39-8cropped6966416360302249442.png",
      description:
        "https://azurekong.hertzai.com/mkt-azure/examples/43932170comfyinstant_235bda39-8cropped6966416360302249442_pred_fls_56c3e733__Abdul_Kalam-_English_Motivational_Speech_audio_embed.mp4",
      image_name: "comfyinstant_235bda39-8cropped6966416360302249442.png",
      fillers: [
        {
          text: "",
          video_link:
            "https://azurekong.hertzai.com/mkt-azure/examples/comfyinstant_235bda39-8cropped6966416360302249442_pred_fls_Blank_audio_embed.mp4",
          type: "idle",
        },
      ],
    },

    {
      prompt_id: 48,
      name: "Casual Conversation Agent",
      prompt: "Spoken English tutor",
      created_date: "2024-11-11T10:06:26",
      request_id: "6a65e893-644a-4049-8572-202172e50d00",
      is_public: true,
      create_agent: false,
      is_active: true,
      user_id: 10077,
      image_url:
        "http://aws_rasa.hertzai.com:5459/output/8fc43ae2-a124-11ef-a35b-42010aa00006.png",
      teacher_avatar_id: 2735,
      video_url: null,
      video_text: "This is Static Description",
      teacher_image_url:
        "https://azurekong.hertzai.com/mkt-aws/txt/voice_dump/cf9788e4-ccropped_image.png",
      description:
        "https://azurekong.hertzai.com/mkt-azure/examples/77a8e926cf9788e4-ccropped_image_pred_fls_a596bcb8_efe2bbf3-aLily_audio_embed.mp4",
      image_name: "cf9788e4-ccropped_image.png",
      fillers: [
        {
          text: "",
          video_link:
            "https://azurekong.hertzai.com/mkt-azure/examples/cf9788e4-ccropped_image_pred_fls_Blank_audio_embed.mp4",
          type: "idle",
        },
      ],
    },
  ];

  const toggleContent = () => {
    setShowContent((prevState) => !prevState);
  };

  return (
    <>
      {/* `relative` makes this the containing block for the empty-state
          greeting below, which needs to centre on the full viewport width
          rather than on the chat pane. This element spans sidebar + content,
          so it IS that width, and being an ordinary in-flow box it scrolls
          away with the rest of the page. */}
      <div className="relative flex bg-black h-screen mb-6 overflow-hidden">
        {screenWidth > 768 ? (
          <div
            onMouseEnter={toggleContent}
            onMouseLeave={toggleContent}
            /* A real width, not a share of the screen.
             *
             * This was `w-[20%] sm:w-[30%] md:w-[15%] lg:w-[20%]`, a ladder
             * that goes 20 -> 30 -> 15 -> 20. Non-monotonic, so no intent
             * produces that curve, and every step is a PERCENTAGE while
             * everything inside it is fixed px: the heading is text-2xl, the
             * badges are w-32, the avatars w-8.
             *
             * That is why it reads correctly on a wide laptop and clips on a
             * smaller screen. Measured at 844x390 the md step gives 127px,
             * while "HevolveAI" at text-2xl plus the p-4 on both sides needs
             * about 144px, so the trailing letter was cut off. At 2560px the
             * same rule gives 512px and everything fits, which is why this
             * survived: it is invisible on the machine it was written on.
             *
             * A fixed width makes the pane predictable at every resolution and
             * lets the px-sized contents actually fit. shrink-0 stops the flex
             * row squeezing it when the chat column is wide.
             */
            className={`relative group w-64 lg:w-72 shrink-0 text-gray-300 p-4 flex flex-col h-screen ${
              showContent ? "bg-gray-900" : ""
            }`}
          >
            <div className="flex justify-between items-center mb-4">
              <div className="text-2xl font-semibold text-white">HevolveAI</div>
            </div>

            <div
              className={`absolute left-0 w-full h-screen bg-gray-900 transition-opacity duration-300 ${
                showContent ? "opacity-100 top-4" : "opacity-0 top-0"
              }`}
            >
              <div className="flex justify-between items-center mb-2">
                <div className="text-2xl font-semibold text-white ml-4">
                  HevolveAI
                </div>
              </div>

              <button
                onClick={handleCreateAgentClick}
                className={`flex items-center gap-2 mb-1 ${
                  decryptedUserId && token
                    ? "text-orange-500 hover:text-orange-600"
                    : "text-gray-500 hover:text-gray-400 cursor-not-allowed"
                }`}
                title={
                  decryptedUserId && token
                    ? "Create new Agent"
                    : "Please login to create an agent"
                }
              >
                <Plus className="w-4 h-4" />
                <span>Create new Agent</span>
                {!(decryptedUserId && token) && (
                  <span className="text-xs">(Login required)</span>
                )}
              </button>

              <div className="mb-4 ml-4">
                <h2 className="text-lg mb-2">Starred</h2>
                <div className="text-sm text-gray-500 flex items-center gap-2">
                  <Star className="w-4 h-4" />
                  <span>Star chats you use often</span>
                </div>
              </div>

              <div className="flex-1">
                <h2 className="text-sm mb-2 ml-4">Recents</h2>
                <div className="space-y-1">
                  {items.map((chat, index) => (
                    <div
                      onClick={() => handleButtonClick(chat)}
                      key={index}
                      className="flex items-center ml-1 justify-start gap-1 hover:bg-gray-800 p-2 rounded cursor-pointer"
                    >
                      <img
                        src={
                          chat.teacher_image_url ||
                          chat.image_url ||
                          AgentPoster
                        }
                        alt={chat?.name}
                        className="md:w-6 md:h-6 lg:w-8 lg:h-8 rounded-full xl:w-8 xl:h-8"
                      />
                      <span className="truncate sm:text-base md:text-[1.2rem]">
                        {chat?.name}
                      </span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => setShowAgentsOverlay(true)}
                  className="text-sm mt-2 ml-2 hover:text-white flex items-center gap-1"
                >
                  View All Agents
                  <ChevronRight className="w-4 h-4" />
                </button>
                <div style={{ display: "flex", gap: "6px", width: "100%" }}>
                  {/* Google Play */}
                  <a
                    href="https://play.google.com/store/apps/details?id=com.hertzai.hevolve"
                    style={{ flex: 1 }}
                  >
                    <div
                      style={{
                        height: "48px",
                        borderRadius: "6px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                        alt="Google Play"
                        style={{
                          height: "100%",
                          width: "auto", // 🔥 important
                        }}
                      />
                    </div>
                  </a>

                  {/* Hevolve */}
                  <a href={downloadInfo.href} download style={{ flex: 1 }}>
                    <div
                      style={{
                        height: "48px",
                        borderRadius: "6px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src="/companion.svg"
                        alt={downloadInfo.label}
                        style={{
                          height: "70%", // 🔥 tweak this (key fix)
                          width: "auto",
                        }}
                      />
                    </div>
                  </a>
                </div>

                <div className="mt-auto space-y-4 absolute bottom-0">
                  <div
                    onClick={toggleDropdown}
                    className="flex items-center gap-2 cursor-pointer mb-5"
                  >
                    <button className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center">
                      <span className="text-white">
                        {decryptedEmail
                          ? decryptedEmail.charAt(0).toUpperCase()
                          : ""}
                      </span>
                    </button>
                    <span className="text-sm truncate">
                      {decryptedEmail && token && decryptedUserId
                        ? decryptedEmail
                        : "Welcome! Log in"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-auto space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center">
                    <span className="text-white">
                      {decryptedEmail
                        ? decryptedEmail.charAt(0).toUpperCase()
                        : ""}
                    </span>
                  </div>
                  <span className="text-sm truncate">
                    {decryptedEmail && token && decryptedUserId
                      ? decryptedEmail
                      : "Welcome! Log in"}
                  </span>
                </div>
                <ChevronRight className="w-4 h-4" />
              </div>
            </div>

            {isOpen && (
              <div
                style={{ border: "1px solid gray" }}
                className="absolute bottom-20 right-0 left-1 border-white w-64 bg-gray-900 text-white rounded-lg p-4 z-10"
              >
                {/* Email Section */}
                <div className="flex items-center gap-2 mb-4 border-b border-gray-700 pb-2">
                  <div className="bg-purple-500 text-white flex items-center justify-center w-8 h-8 rounded-full">
                    R
                  </div>
                  <div>
                    <p className="text-sm font-medium">Personal</p>
                    <p className="text-xs text-gray-400">Pro plan</p>
                  </div>
                </div>

                {/* Options List — see SIDEBAR_LINKS.
                 *
                 * Anchors, not click handlers. These were
                 * onClick={() => navigate(path)}, which is invisible to
                 * everything that is not a left click: no crawler follows it,
                 * middle-click and open-in-new-tab do nothing, copy-link-address
                 * has nothing to copy, and a screen reader announces a list item
                 * where a link belongs. This page is the site's front door, so
                 * these are seven of the very few internal links it has.
                 *
                 * setIsOpen(false) on every item, where three of seven had it.
                 * Navigating unmounts this page so the difference was not
                 * visible, which is exactly why it drifted. */}
                <ul className="space-y-2">
                  {SIDEBAR_LINKS.map((item) => (
                    <li key={item.to}>
                      <RouterLink
                        to={item.to}
                        title={item.title}
                        onClick={() => setIsOpen(false)}
                        className="cursor-pointer hover:bg-gray-800 p-2 rounded flex items-center gap-2 text-white no-underline"
                      >
                        {item.label}
                      </RouterLink>
                    </li>
                  ))}
                </ul>

                <div className="flex justify-center items-center mt-4 space-x-2">
                  {decryptedUserId && token ? (
                    <span
                      className="py-[6px] px-4 text-sm text-center rounded"
                      style={{
                        background:
                          "linear-gradient(to right, #00e89d, #0078ff)",
                        color: "#FFFAE8",
                        cursor: "pointer",
                      }}
                      onClick={LogOutUser}
                    >
                      Logout
                    </span>
                  ) : (
                    <>
                      <RouterLink onClick={() => setIsModalOpen(true)}>
                        <span
                          className="py-[6px] px-4 text-sm text-center rounded"
                          style={{
                            background:
                              "linear-gradient(to right, #00e89d, #0078ff)",
                            color: "#FFFAE8",
                            cursor: "pointer",
                          }}
                        >
                          Login
                        </span>
                      </RouterLink>
                      <OtpAuthModal
                        isOpen={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        message={sessionExpiredMessage}
                      />
                      <RouterLink
                        to="#signup-section"
                        className="py-[6px] px-4 text-sm text-center rounded font-semibold text-white"
                        style={{
                          background:
                            "linear-gradient(to right, #00e89d, #0078ff)",
                          cursor: "pointer",
                        }}
                      >
                        Signup
                      </RouterLink>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          // Mobile hamburger menu logic - removed !authFromUrl condition
          <>
            {!isOpen && (
              <button
                onClick={() => setIsOpen(true)}
                className="absolute left-4 top-4 text-white z-50"
              >
                <Menu className="w-6 h-6" />
              </button>
            )}

            {isOpen && (
              <div className="absolute left-0 w-full h-screen bg-gray-900 transition-all duration-300 p-2 z-50">
                <div className="flex justify-between items-center mb-1">
                  <h1 className="text-2xl font-semibold text-white">
                    HevolveAI
                  </h1>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="text-white"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <button
                  onClick={handleCreateAgentClick}
                  className={`flex items-center gap-2 mb-1 ${
                    decryptedUserId && token
                      ? "text-orange-500 hover:text-orange-600"
                      : "text-gray-500 hover:text-gray-400 cursor-not-allowed"
                  }`}
                  title={
                    decryptedUserId && token
                      ? "Create new Agent"
                      : "Please login to create an agent"
                  }
                >
                  <Plus className="w-4 h-4" />
                  <span>Create new Agent</span>
                  {!(decryptedUserId && token) && (
                    <span className="text-xs">(Login required)</span>
                  )}
                </button>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Display Mode
                  </label>
                  <select
                    value={mediaMode}
                    onChange={(e) => changeMediaMode(e.target.value)}
                    className="w-full bg-gray-800 text-white border border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:bg-gray-700 transition-colors cursor-pointer"
                  >
                    {mediaModeOptions.map((m) => (
                      <option key={m.value} value={m.value} className="bg-gray-800 text-white">
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Rest of mobile menu content */}
                <div>
                  <h2 className="text-sm mb-2 text-white">Recents</h2>
                  <div className="space-y-0.5">
                    {items.map((chat, index) => (
                      <div
                        key={index}
                        onClick={() => handleButtonClick(chat)}
                        className="flex items-center gap-2 text-white hover:bg-gray-800 p-1 rounded cursor-pointer"
                      >
                        <img
                          src={
                            chat.teacher_image_url ||
                            chat.image_url ||
                            AgentPoster
                          }
                          alt={chat?.name}
                          className="!w-6 !h-6 sm:!w-8 sm:!h-8 md:!w-10 md:!h-10 lg:!w-12 lg:!h-12 xl:!w-14 xl:!h-14 rounded-full"
                        />
                        <span className="truncate text-sm sm:text-base md:text-xl lg:text-2xl xl:text-3xl">
                          {chat?.name}
                        </span>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => setShowAgentsOverlay(true)}
                    className="text-sm mt-2 hover:text-white flex items-center gap-1"
                  >
                    View All Agents
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                <div className="flex flex-col items-center gap-2 w-full">
                  {/* Top CTA Button */}
                  <a
                    href={downloadInfo.href}
                    download
                    className="
      w-full
      h-[48px]                     /* SAME HEIGHT */
      flex items-center justify-center
      bg-gradient-to-r from-blue-500 to-green-500
      text-white
      border border-gray-600
      rounded-lg
      text-sm
      hover:opacity-90
      transition
    "
                  >
                    {downloadInfo.label}
                  </a>

                  {/* App Download Buttons */}
                  <div className="flex gap-2 w-full">
                    {/* Google Play */}
                    <a
                      href="https://play.google.com/store/apps/details?id=com.hertzai.hevolve"
                      className="flex-1"
                    >
                      <div className="h-[48px] flex items-center justify-center rounded-lg ">
                        <img
                          alt="Google Play"
                          src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                          className="h-full w-auto"
                        />
                      </div>
                    </a>

                    {/* Hevolve Companion */}
                    <a href={downloadInfo.href} download className="flex-1">
                      <div className="h-[48px] flex items-center justify-center rounded-lg ">
                        <img
                          alt={downloadInfo.label}
                          src="/companion.svg"
                          className="h-[70%] w-auto" /* 🔥 KEY FIX */
                        />
                      </div>
                    </a>
                  </div>
                </div>

                {/* Same seven destinations as the desktop block, same source.
                    Only the type ramp differs, which is why the class names
                    stay here rather than moving into SIDEBAR_LINKS. */}
                <div className="mt-1">
                  <ul className="space-y-1 text-white">
                    {SIDEBAR_LINKS.map((item) => (
                      <li key={item.to}>
                        <RouterLink
                          to={item.to}
                          title={item.title}
                          onClick={() => setIsOpen(false)}
                          className="cursor-pointer text-sm sm:text-base md:text-xl lg:text-2xl xl:text-3xl hover:bg-gray-800 p-1 rounded flex items-center gap-2 text-white no-underline"
                        >
                          {item.label}
                        </RouterLink>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="left-4 flex items-center gap-2 cursor-pointer mt-2">
                  <button className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center">
                    <span className="text-white">
                      {decryptedEmail
                        ? decryptedEmail.charAt(0).toUpperCase()
                        : ""}
                    </span>
                  </button>
                  <span className="text-sm text-white">{decryptedEmail}</span>
                </div>

                <div className="mt-1 flex justify-center gap-1">
                  {!(decryptedUserId && token) && (
                    <ul className="buy-button list-none mb-0">
                      <li className="inline mb-0">
                        {/* Opens the OTP modal this page already mounts.

                            Two wrong answers preceded this one. It started as
                            `to="/login"` with a "// or whatever your login
                            route is" placeholder still attached; hevolve.ai
                            has no /login among its 103 routes, so it hit the
                            path="*" catch-all and rendered the 404 page. It
                            only shows when logged out, which is why nobody
                            testing while signed in ever saw it.

                            The fix for that sent users to
                            hevolvechat.hertzai.com/signin, which is worse than
                            a 404 because it looks like it worked. That page is
                            a DEVELOPER credentials form — it asks for Client ID
                            and Client Secret, and its "Don't have an account?
                            Sign Up" link is href="#" with no handler, verified
                            by clicking it: the URL gains a '#' and nothing
                            renders. A consumer arriving there cannot sign up
                            and has no way onward.

                            The actual signup surface is OtpAuthModal, which
                            this component already imports and already opens
                            from three other buttons via the same
                            isModalOpen state. Linking to another origin was a
                            second path to a thing that was already here. */}
                        <RouterLink
                          to="/signup"
                          onClick={(e) => { e.preventDefault(); setIsModalOpen(true); }}
                          className="py-[6px] px-4 inline-block text-sm text-center rounded"
                          style={{
                            background:
                              "linear-gradient(to right, #00e89d, #0078ff)",
                            borderColor: "#00f0c5",
                            color: "#FFFAE8",
                            cursor: "pointer",
                            transition: "background-color 0.3s ease",
                          }}
                        >
                          Login
                        </RouterLink>
                      </li>

                      <span className="mx-4">OR</span>

                      <li className="inline mb-0 ps-1">
                        <RouterLink
                          to="#signup-section"
                          onClick={() => {
                            const element =
                              document.getElementById("signup-section");
                            element?.scrollIntoView({ behavior: "smooth" });
                          }}
                          className="py-[6px] px-4 inline-block text-sm text-center rounded text-white font-semibold"
                          style={{
                            background:
                              "linear-gradient(to right, #00e89d, #0078ff)",
                            borderColor: "#FFFAE8",
                            cursor: "pointer",
                            transition: "background-color 0.3s ease",
                          }}
                        >
                          Signup
                        </RouterLink>
                      </li>
                    </ul>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        <div className="flex flex-col h-screen bg-black w-full   ">
          <div className="w-full flex flex-col md:flex-row-reverse overflow-auto h-screen">
            {/* Chat/Messages section - Now on the left for wider screens */}

            <div
              className={`${mediaPaneClass} h-[35vh] mb-4 md:h-auto md:mb-0 flex justify-center items-center`}
            >
              {!isTextMode && (
                <>
                  {!uploadedImage && !uploadedPdf ? (
                    <>
                      {videoUrl ? (
                        <video
                          src={videoUrl}
                          width={getVideoWidthforMobile()}
                          onLoadedMetadata={handleLoadedMetadata}
                          className={`absolute top-0 md:top-auto md:bottom-44 md:right-5 object-contain rounded-lg animate-fade-in`}
                          autoPlay
                          ref={videoRef}
                          muted
                          controlsList="nodownload noplaybackrate"
                          controls={false}
                          onEnded={handleMediaEnded}
                        />
                      ) : (mediaMode === 'audio' || audioUrl) ? (
                        // Audio mode (parity with Nunba): prominent
                        // center-stage visualizer that animates whether
                        // or not an audioUrl is currently loaded — the
                        // user sees the hive "listening" between turns.
                        // Falls back to the legacy bottom-right pip when
                        // mediaMode != 'audio' but audioUrl exists
                        // (server-pushed audio in text/video modes).
                        mediaMode === 'audio' ? (
                          <>
                            {audioUrl && (
                              <audio
                                ref={audioRef}
                                src={audioUrl}
                                autoPlay
                                controlsList="nodownload noplaybackrate"
                                controls={false}
                                onLoadedMetadata={handleLoadedMetadataaudio}
                                onEnded={handleMediaEnded}
                                style={{ display: 'none' }}
                              />
                            )}
                            <div className={`absolute top-0 inset-x-0 h-[35vh] md:inset-0 md:h-auto flex justify-center items-center pointer-events-none`}>
                              <VoiceVisualizer
                                audioRef={audioRef}
                                isActive={isPlayingResponse}
                                size={
                                  // screenWidth/Height, not window.*. This is a
                                  // canvas pixel size, so it cannot become a
                                  // CSS variant like the classes around it did.
                                  // Reading React state keeps it in step with
                                  // the resize listener instead of happening to
                                  // be fresh because something else re-rendered.
                                  //
                                  // Bound by the SHORT side. The canvas is
                                  // square and this pane is `md:w-1/3` beside a
                                  // `h-[35vh]` block, so in landscape the height
                                  // runs out long before the width does. Sizing
                                  // from width alone asked for ~200px inside a
                                  // pane ~136px tall, and the cap then shrank it
                                  // again: the visualizer looked small on
                                  // exactly the screens with the most room.
                                  // Ask generously; the pane clamps it.
                                  //
                                  // Sizing from the VIEWPORT was the mistake in
                                  // the first attempt: on an 844x390 landscape
                                  // screen the pane is 196x276, so a
                                  // viewport-derived 120px used 43% of the
                                  // height that was actually there. The pane's
                                  // size is a layout outcome this component
                                  // cannot compute, so it asks for the largest
                                  // sensible canvas and lets maxWidth/maxHeight
                                  // and aspectRatio fit it.
                                  screenWidth <= 768 ? 320 : 400
                                }
                              />
                            </div>
                          </>
                        ) : (
                          <div className="fixed bottom-24 right-4 z-50 flex flex-col items-center">
                            <VoiceVisualizer
                              audioRef={audioRef}
                              isActive={isPlayingResponse}
                              size={120}
                            />
                            <audio
                              ref={audioRef}
                              src={audioUrl}
                              autoPlay
                              controlsList="nodownload noplaybackrate"
                              controls={false}
                              onLoadedMetadata={handleLoadedMetadataaudio}
                              onEnded={handleMediaEnded}
                            />
                          </div>
                        )
                      ) : null}
                    </>
                  ) : (
                    <div
                      className={`h-full md:h-[85vh] w-full flex flex-col items-center justify-between gap-4`}
                    >
                      <>
                        {videoUrl ? (
                          <div className="w-full">
                            <video
                              src={videoUrl}
                              width={getVideoWidthforMobile()}
                              height="auto"
                              className={`h-[15vh] md:h-[40vh] w-full object-contain rounded-lg animate-fade-in`}
                              autoPlay
                              ref={videoRef}
                              muted
                              controlsList="nodownload noplaybackrate"
                              controls={false}
                              onLoadedMetadata={handleLoadedMetadata}
                              onEnded={handleMediaEnded}
                            />
                          </div>
                        ) : audioUrl ? (
                          <div className="w-full">
                            <div className="flex items-center space-x-4 justify-center mt-5 px-4 py-4">
                              <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center">
                                <button className="text-gray-700 text-xl">
                                  ▶️
                                </button>
                              </div>
                            </div>
                            <div className="mt-4">
                              <audio
                                ref={audioRef}
                                src={audioUrl}
                                className="w-full"
                                autoPlay
                                controlsList="nodownload noplaybackrate"
                                controls={false}
                                onLoadedMetadata={handleLoadedMetadataaudio}
                                onEnded={handleMediaEnded}
                              />
                            </div>
                          </div>
                        ) : null}
                      </>

                      {(uploadedImage || uploadedPdf) && (
                        <div className="fixed top-5 bg-black bg-opacity-75 flex justify-center items-center z-50">
                          <div className="relative bg-white p-4 rounded-lg shadow-lg w-[90%] max-w-4xl">
                            {/* Close Button */}
                            <button
                              onClick={() => {
                                setUploadedImage(null);
                                setUploadedPdf(null);
                              }}
                              className="absolute top-2 right-2 bg-gray-300 hover:bg-gray-400 rounded-full p-2"
                            >
                              ✕
                            </button>

                            {/* Image Display */}
                            {uploadedImage && (
                              <div className="w-full">
                                <img
                                  src={uploadedImage}
                                  alt="Content Image"
                                  className={`h-[60vh] md:h-[70vh] w-full object-contain rounded-lg`}
                                />
                              </div>
                            )}

                            {/* PDF Display */}
                            {uploadedPdf && (
                              <div
                                ref={containerRef}
                                className="relative w-full border-2 border-gray-200 rounded-lg flex justify-center items-center h-[75vh]"
                              >
                                <React.Suspense fallback={null}>
                                  <PdfPreview
                                    file={uploadedPdf}
                                    pageNumber={currentPage}
                                    scale={scale}
                                    onLoadSuccess={onDocumentLoadSuccess}
                                  />
                                </React.Suspense>

                                {/* Pagination */}
                                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white shadow-lg rounded-full flex items-center">
                                  <button
                                    onClick={prevPage}
                                    className="px-3 py-2 bg-gray-300 rounded-full flex items-center justify-center cursor-pointer"
                                  >
                                    <ChevronLeft size={16} />
                                  </button>
                                  <span className="text-sm font-medium px-2">
                                    Page {currentPage} of {numPages}
                                  </span>
                                  <button
                                    onClick={nextPage}
                                    className="px-3 py-2 bg-gray-300 rounded-full flex items-center justify-center cursor-pointer"
                                  >
                                    <ChevronRight size={16} />
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {codeContent && (
                <div className="mt-4 bg-black p-4 rounded-lg shadow-md">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Code Snippet
                  </h3>
                  <pre className="bg-gray-100 p-2 rounded-md text-sm overflow-auto">
                    <code>{codeContent}</code>
                  </pre>
                  <button
                    onClick={() => setCodeContent(null)}
                    className="mt-2 bg-red-500 text-white px-3 py-1 rounded-md"
                  >
                    Close Code
                  </button>
                </div>
              )}
            </div>

            <div
              className={`h-[50vh] overflow-x-hidden md:h-auto md:overflow-x-visible flex-1 w-full ${chatPaneClass}`}
            >
              {messages.length === 0 ? (
                <div className="h-5/6 flex items-center justify-center pt-16">
                  {/* pt-16 clears the floating top bar. The greeting is centred in
                      5/6 of the pane, which on a 390px-tall landscape phone put it
                      at y=23 while the "Download Nunba & HARTOS" bar occupies y=16
                      to 54, so the heading rendered underneath it. Measured, not
                      guessed: the two rects overlapped by 31px. Padding rather than
                      margin so the flex centring re-runs inside the reduced box.

                      NOT md:pt-0. A landscape phone at 844x390 is WIDER than
                      768px, so it matches md: and would have had the clearance
                      cancelled on the exact viewport that needs it. md is a
                      width query; short-and-wide is the case it cannot see. The
                      padding is unconditional: on a tall screen the block is
                      centred in 5/6 of the height and 64px changes nothing that
                      matters. */}
                  {/* Centred on the VIEWPORT, not on the chat pane.

                      In flow this stack centres inside the chat column, and
                      that column is not centred on screen: the sidebar takes
                      space on the left and the media pane takes a different
                      amount on the right. Measured at 1366 the stack landed at
                      x-centre 610 against a viewport centre of 683, so it had
                      202px of gap on its left and 579px on its right. The same
                      offset at 844 was 56px. Neither is symmetric, and no
                      arrangement of the two panes makes it symmetric because
                      the sidebar and the media pane are different widths.

                      This also removes the horizontal jump in the hero
                      cross-fade. The marketing hero centres its wordmark on the
                      viewport, so with the greeting at 610 the word "Hevolve"
                      slid 73px sideways while fading. Both now resolve against
                      the same axis, so it fades in place.

                      ABSOLUTE, not fixed. I used `fixed` first and it was a
                      real regression, visible the moment anyone scrolled: a
                      fixed box is pinned to the VIEWPORT, so the wordmark and
                      greeting hung motionless in mid-screen while the page ran
                      underneath them, with the input card stranded across the
                      Login/Signup buttons. The layout audit never scrolls and
                      neither did my screenshots, so nothing I ran could see it.

                      `absolute` against the section wrapper gives the same
                      centring and scrolls away correctly, because that wrapper
                      spans sidebar + content (so inset-x-0/mx-auto resolves
                      against the full viewport width) and is itself an ordinary
                      in-flow box that moves with the page.

                      Not a full-bleed margin trick either: that arithmetic only
                      lands on the viewport centre when the containing block is
                      itself centred, and this one is offset by the sidebar. */}
                  <div className="text-center space-y-4 mb-1 absolute inset-x-0 mx-auto w-fit top-1/2 -translate-y-1/2">
                    {/* Sized by height as well as width. The class ladder
                        scales on viewport WIDTH only, so a landscape phone at
                        844x390 matched md: and rendered a 60px heading into
                        390px of height, clipping it against the top bar. clamp
                        against vh keeps it large on a desktop and lets it shrink
                        when there is no vertical room; the classes stay as the
                        width-based ceiling. */}
                    <h1
                      className="animate-fade-in text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold leading-tight mb-3"
                      style={{ fontSize: 'clamp(2rem, 9vh, 6rem)' }}
                    >
                      {currentAgent?.name}
                    </h1>
                    <h2 className="text-2xl font-medium text-white-900 animate-fade-in">
                      How can I help you today?
                    </h2>
                    <p className="text-white-600 animate-fade-in">
                      I'm an AI agent. I'm here to help with all kinds of tasks.
                    </p>
                    <p className="text-white-600 animate-fade-in">
                      {currentAgent?.video_text ===
                        "This is Static Description" ||
                      agentData?.video_text === "This is Static Description"
                        ? ""
                        : currentAgent?.video_text}
                    </p>
                    {!(decryptedUserId && token) && (
                      <ul className="buy-button list-none mb-0">
                        <li className="inline mb-0">
                          <RouterLink
                            to="/signup"
                            onClick={(e) => { e.preventDefault(); setIsModalOpen(true); }}
                            className="py-[6px] px-4 inline-block text-sm text-center rounded"
                            style={{
                              background:
                                "linear-gradient(to right, #00e89d, #0078ff)",
                              borderColor: "#00f0c5",
                              color: "#FFFAE8",
                              cursor: "pointer",
                              transition: "background-color 0.3s ease",
                            }}
                          >
                            Login
                          </RouterLink>
                          <OtpAuthModal
                            isOpen={isModalOpen}
                            onClose={() => setIsModalOpen(false)}
                          />
                        </li>
                        <span className="mx-4">OR</span>
                        <li className="inline mb-0 ps-1">
                          <RouterLink
                            onClick={() => {
                              const element =
                                document.getElementById("signup-section");
                              element?.scrollIntoView({ behavior: "smooth" });
                            }}
                            to="#signup-section"
                            className="py-[6px] px-4 inline-block text-sm text-center rounded text-white font-semibold"
                            style={{
                              background:
                                "linear-gradient(to right, #00e89d, #0078ff)",
                              borderColor: "#FFFAE8",
                              cursor: "pointer",
                              transition: "background-color 0.3s ease",
                            }}
                          >
                            Signup
                          </RouterLink>
                        </li>
                      </ul>
                    )}
                  </div>
                </div>
              ) : (
                <div className="max-w-5xl mx-auto p-4 space-y-6 h-full overflow-y" role="log" aria-live="polite">
                  {messages.map((message, index) => {
                    if (message.type === "thinking_container") {
                      return (
                        <ThinkingProcessContainer
                          key={`thinking-container-${message.id}-${index}`}
                          thinkingMessages={message.thinkingSteps}
                          isMainExpanded={message.isMainExpanded}
                          isContainerCompleted={message.isCompleted} // ADD THIS LINE
                          onToggleMain={() => {
                            setMessages((prev) =>
                              prev.map((msg, msgIndex) =>
                                msgIndex === index
                                  ? {
                                      ...msg,
                                      isMainExpanded: !msg.isMainExpanded,
                                    }
                                  : msg,
                              ),
                            );
                          }}
                          onToggleIndividual={(stepId) => {
                            setMessages((prev) =>
                              prev.map((msg, msgIndex) =>
                                msgIndex === index
                                  ? {
                                      ...msg,
                                      thinkingSteps: msg.thinkingSteps.map(
                                        (step) =>
                                          step.id === stepId
                                            ? {
                                                ...step,
                                                isExpanded: !step.isExpanded,
                                              }
                                            : step,
                                      ),
                                    }
                                  : msg,
                              ),
                            );
                          }}
                        />
                      );
                    }

                    return (
                      <div
                        key={index}
                        className="rounded-lg p-6 shadow-sm overflow-auto"
                        style={{
                          backgroundColor:
                            message.type === "user" ? "#EFEAAA" : "#212A31",
                          color:
                            message.type === "user" ? "#000000" : "#FFFFFF",
                        }}
                      >
                        <div className="flex-1 space-y-4">
                          <div
                            className={
                              message.type === "user"
                                ? "text-black font-bold"
                                : "text-white"
                            }
                          >
                            {message.type === "user" && (
                              <div className="space-y-2">
                                <div>{message.content}</div>

                                {message.pdf && (
                                  <div className="flex flex-col gap-2 mt-2">
                                    <div className="flex items-center gap-2">
                                      <FileText className="w-5 h-5 text-gray-500" />
                                      <span className="text-sm text-gray-700">
                                        Understanding the Content: {progress}%
                                      </span>
                                    </div>

                                    <div className="w-full h-4 bg-gray-200 rounded-full overflow-hidden">
                                      <div
                                        className="h-full bg-blue-600 transition-all duration-300 ease-out rounded-full"
                                        style={{
                                          width: `${Math.min(
                                            100,
                                            Math.max(0, progress),
                                          )}%`,
                                        }}
                                      />
                                    </div>

                                    <button
                                      onClick={() =>
                                        handlePdfClick(message.pdf)
                                      }
                                      className="text-blue-600 hover:text-blue-800 underline flex items-center gap-2"
                                    >
                                      <FileText className="w-5 h-5" />
                                      View Uploaded PDF
                                    </button>
                                  </div>
                                )}

                                {message.image && (
                                  <div className="mt-2">
                                    <img
                                      src={message.image}
                                      alt="Uploaded"
                                      className="w-16 h-16 rounded-lg shadow-md cursor-pointer"
                                      onClick={() =>
                                        handleImageClick(message.image)
                                      }
                                    />
                                  </div>
                                )}
                              </div>
                            )}

                            {message.type === "assistant" && (
                              <>
                                {isTextMode ? (
                                  <div>{message.content}</div>
                                ) : animatingMessageIndex === index &&
                                  duration > 0 ? (
                                  <TypeWriterForSubtitle
                                    text={message.content}
                                    duration={duration}
                                    isIdle={isIdleVideo(videoUrl)}
                                  />
                                ) : (
                                  <div>{message.content}</div>
                                )}
                                {/* Draft-first: visual indicator while expert response is pending */}
                                {message.isDraft && (
                                  <div className="flex items-center gap-1 mt-1 text-xs text-amber-400 opacity-70">
                                    <span className="inline-block w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                                    Draft, upgrading...
                                  </div>
                                )}
                              </>
                            )}
                          </div>

                          {message.code && (
                            <button
                              onClick={() => setCodeContent(message.code)}
                              className="bg-blue-500 text-white px-3 py-1 rounded-md"
                            >
                              Show Code
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {loading && !currentThinkingId && (
                    <div className="flex items-center justify-start space-x-3">
                      <span className="w-2 h-2 bg-gray-500 rounded-full animate-pulse"></span>
                      <span className="w-2 h-2 bg-gray-500 rounded-full animate-pulse delay-200"></span>
                      <span className="w-2 h-2 bg-gray-500 rounded-full animate-pulse delay-400"></span>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>
          </div>

          <div className="bg-black pt-4 border-t border-gray-700 flex justify-center flex-col mt-2 mb-2">
            {pdfFile && (
              <div className="relative inline-block mt-4">
                <span className="text-gray-700">
                  Uploaded PDF: {pdfFile.name}
                </span>
                <button
                  onClick={handleRemovePdf}
                  className="text-red-600 hover:text-red-800 p-1 ml-2"
                >
                  Remove
                </button>
              </div>
            )}
            {userImage && (
              <div className="relative inline-block mt-4">
                <span className="text-gray-700"> Uploaded Image: </span>
                <img
                  src={userImage}
                  alt="Uploaded"
                  className="w-16 h-16 object-cover rounded-lg shadow-md mt-2"
                />
                <button
                  onClick={handleRemoveImage}
                  className="text-red-600 hover:text-red-800 p-1 ml-2"
                >
                  Remove
                </button>
              </div>
            )}

            <div className="flex items-center w-[95%] relative ml-1">
              {/* The textarea gets its own positioning context so the
                  click-catcher below covers the input and NOTHING else. The
                  five icon buttons after it are siblings in this same flex row,
                  so an overlay spanning the row would sit on top of them and
                  make attach, mic and send unclickable. */}
              <div className="relative flex-1">
              <textarea
                ref={textareaRef}
                disabled={!(decryptedUserId && token)}
                value={inputMessage}
                onChange={(e) => {
                  setInputMessage(e.target.value);
                  e.target.style.height = "auto";
                  e.target.style.height = `${e.target.scrollHeight}px`;

                  if (e.target.scrollHeight > 200) {
                    e.target.style.height = "200px";
                    e.target.style.overflowY = "scroll";
                  } else {
                    e.target.style.overflowY = "hidden";
                  }
                }}
                placeholder={
                  decryptedUserId && token
                    ? "Message..."
                    : "Please login to talk to agent."
                }
                onFocus={handleFocus}
                onBlur={handleBlur}
                onKeyPress={handleKeyPress}
                className="w-full text-black border bg-[#fff8ea] text-base border-gray-200 rounded-lg px-4 py-3 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 break-words overflow-wrap-anywhere whitespace-pre-wrap"
                style={{
                  minHeight: "44px",
                  maxHeight: "200px",
                  wordWrap: "break-word",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                  whiteSpace: "pre-wrap",
                }}
              />

              {/* An invisible click target over the disabled textarea, so that
                  clicking the box still opens the login modal.

                  This is the affordance the old floating button carried, and
                  it cannot be restored by putting onClick on the wrapper: a
                  DISABLED form control dispatches no click event at all and
                  nothing bubbles to its ancestors, so a handler up there never
                  fires. Verified by clicking the textarea in a real browser and
                  watching for the modal: it did not appear. The old button
                  worked precisely because it was a separate element ON TOP of
                  the input, which is what this reproduces, minus the black
                  background that was covering the input and the account row. */}
              {!(decryptedUserId && token) && (
                <div
                  className="absolute inset-0 cursor-pointer"
                  onClick={() => setIsModalOpen(true)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setIsModalOpen(true);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label="Log in to talk to the agent"
                />
              )}
              </div>

              {/* The "please login" hint is the textarea's placeholder now,
                  not a floating black box.

                  It was `absolute top-[40px] left-0`, which put it 40px below
                  the top of this wrapper and therefore across the very input it
                  was explaining, covering the account row beside it. Moving it
                  to bottom-full lifted it off the input and straight onto the
                  Login/Signup buttons at 844x390: a 390px-tall viewport has no
                  free band for a floating hint, so every position is on top of
                  something.

                  A placeholder cannot collide with anything, needs no
                  positioning, and appears exactly where someone who is trying
                  to type will look. The textarea is already disabled while
                  logged out, so the two states agree. Nothing is lost by
                  dropping the button: it opened the login modal, and the empty
                  state's Login/Signup pair and the sidebar account row both
                  still do that. */}

              <button
                onClick={() => document.getElementById("fileInput").click()}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <Image className="w-5 h-5" />
              </button>

              <button
                onClick={() => document.getElementById("pdfInput").click()}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <FileText className="w-5 h-5" />
              </button>
              {!isRecording ? (
                <button
                  onClick={handleStart}
                  className="text-green-500 hover:text-green-700 p-1 flex items-center gap-1"
                >
                  <Mic className="w-5 h-5" />
                </button>
              ) : (
                <button
                  onClick={handleStop}
                  className="text-red-500 hover:text-red-700 p-1 flex items-center gap-1"
                >
                  <span>Stop</span>
                </button>
              )}

              <button
                disabled={!(decryptedUserId && token)}
                onClick={handleSend}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <SendHorizontal className="w-5 h-5" />
              </button>

              <input
                type="file"
                id="fileInput"
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
              />
              <input
                type="file"
                id="pdfInput"
                accept="application/pdf"
                onChange={handlePdfSelect}
                className="hidden"
              />
            </div>
          </div>
        </div>
      </div>
      {!(decryptedUserId && token) && <NewHome />}

      {showAgentsOverlay && (
        <Agents
          isOverlay={true}
          onClose={() => setShowAgentsOverlay(false)}
          onAgentSelect={handleAgentSelect}
          predefinedAgents={allAgents}
        />
      )}
      {showCreateAgentForm && (
        <CreateAgentForm
          onClose={() => setShowCreateAgentForm(false)}
          onSubmit={handleCreateAgentSubmit}
          userId={decryptedUserId}
        />
      )}

      {screenWidth >= 768 && (
        <div className="absolute top-4 right-44 z-500">
          <select
            // Controlled, and routed through changeMediaMode like the mobile
            // menu. It was neither: uncontrolled, so it could not show a mode
            // set anywhere else, and wired to the isTextMode shim, which maps
            // false to 'video' -- so "Default Mode (audio only)" selected VIDEO.
            value={mediaMode}
            onChange={(e) => changeMediaMode(e.target.value)}
            className="
                bg-gray-800 
                text-white 
                border border-gray-600 
                rounded-lg 
                px-3 py-2 
                text-sm 
                focus:outline-none 
                focus:ring-2 
                focus:ring-blue-500 
                focus:border-transparent
                hover:bg-gray-700
                transition-colors
                cursor-pointer
                min-w-[120px]
            "
          >
            {mediaModeOptions.map((m) => (
              <option key={m.value} value={m.value} className="bg-gray-800 text-white">
                {m.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Companion Status */}
      <div className="absolute top-0 right-8 z-500 flex items-center space-x-2">
        {companionStatus && (
          <img
            src={companionStatus.isRunning ? connectedImg : DisconnectedImg}
            alt="Connection Status"
            className="w-16 h-16"
            title={
              companionStatus.isRunning
                ? "Connected"
                : "Nunba Companion App Disconnected"
            }
          />
        )}
      </div>

      {/* Creation Mode Animation */}
      <div className="absolute top-0 right-24 z-50 flex items-center space-x-2">
        {currentAgent?.agent_status === "Creation Mode" && (
          <div className="relative w-16 h-16">
            <React.Suspense fallback={null}>
              <Lottie
                animationData={creationModeAnimation}
                loop={true}
                autoplay={true}
                className="w-16 h-16"
                title="Agent Creation Mode"
              />
            </React.Suspense>
            <span className="absolute inset-0 flex items-center justify-center text-white text-[8px] font-medium pointer-events-none">
              Creation mode
            </span>
          </div>
        )}
      </div>

      {screenWidth >= 768 && !companionStatus.isInstalled ? (
        <div className="absolute top-4 right-96 z-50 mr-3">
          <a
            href={downloadInfo.href}
            download
            className="
          bg-gradient-to-r from-blue-500 to-green-500
          text-white
          border border-gray-600
          rounded-lg
          px-3 py-2
          text-sm
          focus:outline-none
          focus:ring-2
          focus:ring-blue-500
          focus:border-transparent
          hover:bg-gray-700
          transition-colors
          cursor-pointer
          min-w-[120px]
          inline-block
          text-center
        "
          >
            {downloadInfo.label}
          </a>
        </div>
      ) : (
        screenWidth >= 768 &&
        !companionStatus.isRunning && (
          <div className="absolute top-4 right-96 z-50 mr-3">
            <a
              href="hevolveai://launch?action=show"
              className="
            bg-gradient-to-r from-green-500 to-blue-500
            text-white
            border border-gray-600
            rounded-lg
            px-3 py-2
            text-sm
            focus:outline-none
            focus:ring-2
            focus:ring-green-500
            focus:border-transparent
            hover:bg-gray-700
            transition-colors
            cursor-pointer
            min-w-[120px]
            inline-block
            text-center
          "
            >
              Launch Nunba Companion App
            </a>
          </div>
        )
      )}
    </>
  );
};

export default ChatInterface;
